import SalesExtrasPicker from "../components/pos/SalesExtrasPicker";
import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, idr, pesanGagalDariServer } from "../lib/api";
import {
  Plus, Trash2, X, CheckCircle2, XCircle, Pencil, Send, Clock, User, ShoppingCart,
  AlertTriangle, FileText, Eye, CreditCard, Repeat, Phone, MapPin, StickyNote,
  Sparkles, Lock, Ticket,
} from "lucide-react";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import AIProductChat from "../components/hris/AIProductChat";
import ItemCariField from "../components/pos/ItemCariField";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { useGudangOptions, wajibPilihGudang, pesanGudangButaArah } from "../lib/gudang";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import {
  PaymentMethodSelect, MetodePembayaranManager, TombolKelolaMetode, bolehKelolaMetode,
} from "../components/pembayaran/MetodePembayaran";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi,
} from "@/components/ui/responsive-table";
import SourceInfoField, { useSourceInfoConfig, validasiSourceInfo } from "../components/SourceInfoField";
import PilihPelangganKartu, { kartuCocok } from "../components/pos/PilihPelangganKartu";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import {
  KATEGORI_ITEM, KATEGORI_DEFAULT, labelKategori, normalKategori, deriveKode, deriveKategoriTeksBebas,
} from "@/lib/kategoriBarang";
import BundleKomponenPicker, {
  useResepBundlingAktif, useProdukInfoCache,
  cariAlasanBundleBelumSiap, kumpulkanSemuaImeiForm,
} from "@/components/bundling/BundleKomponenPicker";
import {
  TI_STATUS, TI_KELENGKAPAN, MIN_DIGIT_HP, rakitNamaTradeIn, barisPembayaranBaru, metodeTradeIn,
  jumlahDiskonBaris, cekDiskonBaris, digitHp, susunPayloadNota, bodyPratinjauNota,
  selisihTotalKasir, totalKasirCocok, patchBarisDenganKunciPaket, hargaPaketTerkunci,
  pisahDpMenurutHp,
} from "@/lib/notaPenjualan";

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: new Date().toTimeString().slice(0, 5),
  nama_pelanggan: "", no_hp: "",
  // B2: kartu pelanggan yang dipilih ({nama, no_hp}) — hanya penanda layar,
  // DIBUANG dari payload sebelum dikirim (lihat submit).
  _kartu_pelanggan: null,
  // `diskon` = diskon PRODUK per baris dalam rupiah (brief 22 Sep D5).
  items: [{ nama_barang: "", harga: 0, diskon: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, non_stok: false, komponen: null }],
  imei: "", metode_pembayaran: "Cash",
  // Format nota baru (D4): baris pertama = metode pembayaran utama + nominal;
  // baris berikutnya = "tambah pembayaran" (keterangan | metode | nominal).
  // Tidak ada lagi mode "satu metode tanpa nominal" maupun kolom Harga Terima:
  // uang nota = jumlah baris ini (+ trade-in + DP), ditegakkan server.
  payments: [barisPembayaranBaru("Cash")],
  trade_in: false, trade_in_note: "",
  trade_in_items: [{ nama: "", status: "", warna: "", bh: "", kelengkapan: "",
                     imei: "", catatan: "", harga: 0 }],
  catatan: "", nama_penjual: "", branch: "",
  // "Total Pembayaran" (D1/D6) — hanya bisa diisi KASIR (`summary.is_kasir`).
  // "" = belum diisi (kasir akan mengisinya di dialog Setujui); 0 adalah angka
  // sah untuk nota bertempo yang belum dibayar sepeser pun (D2).
  total_pembayaran_kasir: "",
  // Satu nota = satu gudang (kontrak G1 keputusan owner #4) — "" berarti
  // biarkan backend menurunkan sendiri (lihat useGudangOptions). Hanya
  // relevan dipilih manual kalau `tampilkanPilihGudang` true.
  warehouse_id: "",
  // Jenis pelanggan & tempo (owner 29 Agu 2026). Bawaan "umum" tanpa tempo =
  // perilaku lama persis.
  jenis_pelanggan: "umum", tempo_pembayaran: "", pic_approval_piutang: "",
  // Diskon NOTA & biaya admin DIHAPUS dari Input Nota (brief 22 Sep D2/D4) —
  // medannya tetap ada HANYA supaya draf LAMA yang diedit mengirim ulang nilai
  // tersimpannya apa adanya (tampil baca-saja). Nota baru selalu 0/"".
  // `diskon_pic_approval` kini PIC untuk diskon PRODUK per baris (D5).
  diskon: 0, diskon_pic_approval: "",
  biaya_admin_mode: "", biaya_admin_nilai: 0,
  // Rekening pengembalian DOWNGRADE — form baru tidak lagi mengisinya karena
  // downgrade diblokir (D10); draf lama mengirim ulang nilai tersimpannya.
  refund_nama_bank: "", refund_no_rekening: "", refund_atas_nama: "",
  dp_id: "", dp_nominal: "",
  // PPN (keputusan owner 18 Agu 2026) — toggle tingkat DOKUMEN. Tarif &
  // nominal DIHITUNG SERVER (pratinjau & approve), bukan di layar ini.
  kena_ppn: false,
  // Sumber Info / atribusi marketing (brief bos 11 Agu 2026, WAJIB selama
  // saklar darurat aktif — lihat components/SourceInfoField.jsx). "" (bukan
  // null) supaya <select> terkontrol dari awal render.
  source_info: "", source_ref_id: "",
  // Kode tiket antrian (brief 15 Sep G5/G6). Kosong = nota tanpa tiket,
  // perilaku lama persis; dikirim sebagai null.
  kode_antrian: "",
});

/** Status wajib-scan milik pengguna ini, atau null kalau tidak bisa diambil.
 *
 *  403 dan galat jaringan APA PUN sengaja dianggap "tidak wajib" — cermin
 *  fail-open backend. Kalau layar fail-closed sementara server fail-open,
 *  kasir terkunci dari Input Nota gara-gara sinyal jelek padahal server
 *  sendiri akan menerima notanya.
 */
async function ambilTiketSaya() {
  try {
    const { data } = await api.get("/antrian/tiket-saya");
    return data || null;
  } catch {
    return null;
  }
}

/** Tiket yang masuk akal untuk nota ini: yang sedang dipegang dulu, lalu
 *  tiket "membeli" hari ini (satu tiket boleh untuk beberapa nota). */
function daftarTiketPilihan(st) {
  const hasil = [];
  const sudah = new Set();
  if (st?.tiket?.kode_antrian) {
    hasil.push({ ...st.tiket, _keterangan: "sedang Anda layani" });
    sudah.add(st.tiket.kode_antrian);
  }
  (st?.tiket_membeli_hari_ini || []).forEach((t) => {
    if (!t?.kode_antrian || sudah.has(t.kode_antrian)) return;
    sudah.add(t.kode_antrian);
    hasil.push({ ...t, _keterangan: `membeli hari ini · ${Number(t.jumlah_nota) || 0} nota` });
  });
  return hasil;
}

/** Nomor antrian untuk kode yang tertaut di form — kode datang dari URL tanpa
 *  nomornya, jadi dicari di status tiket yang sudah termuat. */
function nomorUntukKode(st, kode) {
  if (!kode) return null;
  const t = daftarTiketPilihan(st).find((x) => x.kode_antrian === kode);
  return t?.nomor_antrian || null;
}

const STATUS_META = {
  pending:  { label: "Menunggu Approval", color: "amber",   icon: Clock },
  approved: { label: "Disetujui",         color: "emerald", icon: CheckCircle2 },
  rejected: { label: "Ditolak",           color: "rose",    icon: XCircle },
};

// Pilihan sub-kolom unit trade-in & perakit namanya dipindah ke
// `lib/notaPenjualan.js` (dipakai juga Edit Penjualan) — satu daftar, bukan dua.


export default function InputNota({ unitAwal = null }) {
  const { user, can } = useAuth();
  // Cabang nota (brief 22 Sep D1/D9): HANYA cabang yang punya Kas Toko +
  // penampung trade-in — daftar yang SAMA dengan yang ditegakkan server saat
  // draf disimpan (`branches_master.pastikan_cabang_nota`). Nama toko bawaan
  // ZOA dan cabang yang cuma tercatat di data karyawan tidak ditawarkan: uang
  // nota cabang tanpa akun dulu mendarat di kas bawaan/kliring 1055.
  const BRANCHES = useCabangOptions({ untuk: "nota" });
  // Cermin `payment_methods_module._boleh_kelola` di backend (role lama +
  // izin eksplisit). Daftar role-nya TIDAK lagi ditulis ulang di sini —
  // lihat `components/pembayaran/MetodePembayaran.jsx`.
  const canManageMethods = bolehKelolaMetode(user);
  // Autocomplete produk memakai modul inventory_produk — modul berbeda dari
  // nota_sales. Kalau tidak diizinkan, kolom nama barang tetap ketik bebas
  // (tidak pernah memanggil endpoint lookup, jadi tidak ada 403 mengganggu).
  const bolehLookupProduk = can("inventory_produk", "view");
  // Pemilih IMEI per baris barang (brief bos poin 4: IMEI hanya boleh
  // DIPILIH dari unit yang terdaftar di inventory, bukan diketik bebas).
  // Modul beda lagi dari inventory_produk — kalau tidak diizinkan, baris
  // ber-SKU tetap bisa disimpan TANPA IMEI (backend tidak pernah memaksa
  // IMEI terisi), pemilihnya sekadar tidak muncul.
  const bolehLookupUnit = can("inventory_unit", "view");
  // Pemilih gudang nota (kontrak G1 keputusan owner #4: satu nota = satu
  // gudang) — hanya relevan/tampil saat sales memang berhak atas LEBIH dari
  // satu gudang (lihat docstring useGudangOptions). Berhak 0/1 gudang -> tidak
  // pernah tampil, backend menurunkan gudangnya sendiri saat approve.
  const bolehLihatGudang = can("inventory_gudang", "view");
  const warehouses = useGudangOptions(bolehLihatGudang);
  // Wajib pilih HANYA kalau backend sungguh akan menolak tanpanya — lihat
  // docstring `wajibPilihGudang` (lib/gudang.js). Bukan sekadar
  // `warehouses.length > 1`: admin/superadmin/finance_ceo melihat SEMUA
  // gudang aktif lewat endpoint yang sama, jadi length>1 nyaris selalu true
  // untuk mereka walau backend tidak pernah memaksa mereka memilih.
  const tampilkanPilihGudang = wajibPilihGudang(user, warehouses);
  // Cetak faktur/struk DIHAPUS dari halaman ini (brief bos 21 Agu 2026 #1) —
  // nota yang sudah di-approve otomatis jadi dokumen `sales` dan tercetak
  // dari Input Penjualan (lihat link petunjuk di header di bawah). Dipakai
  // untuk gating link petunjuk itu supaya tidak mengarah ke halaman yang
  // pengguna sendiri tidak punya izin membukanya.
  const bolehLihatPenjualan = can("sales", "view");
  // Sumber Info (brief bos 11 Agu 2026) — saklar darurat + pilihan + daftar
  // KOL/PFP aktif diambil SEKALI di sini (bukan tiap modal dibuka/tiap
  // ketikan), lihat docstring useSourceInfoConfig.
  const sourceInfoCfg = useSourceInfoConfig();

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  // Bawaan false untuk SEMUA orang: non-kasir memang hanya menerima notanya
  // sendiri dari server (`list_nota_sales`), jadi centang ini cuma bermakna
  // bagi kasir — dan kasir melihat semua nota sebagai bawaan, seperti dulu.
  const [showMine, setShowMine] = useState(false);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0 });
  // Brief 22 Sep D1: "kasir" = `_is_kasir` di SERVER (role kasir/admin ATAU
  // izin `nota_sales.approve/update`), dibaca dari `GET /nota/sales/summary/
  // stats` — bukan daftar role yang diketik ulang di layar. Daftar role lama
  // menyembunyikan tombol Setujui dari pemegang izin yang sah, dan
  // menampilkan "Total Pembayaran" ke orang yang kirimannya dibuang server.
  const canApprove = !!summary.is_kasir;

  const [modal, setModal] = useState(false);
  // Paket/bundling (poin 8, brief bos 23 Agu 2026) — resep AKTIF (+ stok
  // virtualnya) dimuat ulang tiap modal dibuka, sama pola & alasan dengan
  // Sales.jsx (lihat docstring BundleKomponenPicker.jsx).
  const { resepMap: resepBundlingMap } = useResepBundlingAktif(bolehLookupProduk && modal);
  const { produkInfoCache, ensureProdukInfo } = useProdukInfoCache(bolehLookupProduk);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Antrian (brief 15 Sep G5/G6). Hanya pemegang antrian.create yang bisa
  // memindai tiket, jadi hanya mereka yang pernah ditanyai status wajib-scan —
  // selain itu halaman ini berperilaku persis seperti dulu.
  const bolehAntrian = can("antrian", "create");
  const bolehBukaAntrian = bolehAntrian || can("antrian", "view");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const kodeDariUrl = searchParams.get("kode_antrian");
  const [statusAntrian, setStatusAntrian] = useState(null);
  const wajibScan = !!statusAntrian?.wajib_scan;
  const [memeriksaAntrian, setMemeriksaAntrian] = useState(false);
  const [konfirmasiTiket, setKonfirmasiTiket] = useState(null); // {pilihan: [...], kode}
  const [scanInline, setScanInline] = useState(false);
  const [pesanTolakAntrian, setPesanTolakAntrian] = useState("");
  const [kodeInline, setKodeInline] = useState("");
  const [busyPanggil, setBusyPanggil] = useState(false);
  // ?kode_antrian hanya boleh membuka modal SEKALI. Tanpa ref, setiap kali
  // daftar karyawan dimuat ulang modalnya terbuka lagi dan menimpa isian.
  const sudahBukaDariUrl = useRef(false);
  const nomorTiketForm = nomorUntukKode(statusAntrian, form.kode_antrian);

  useEffect(() => {
    if (!bolehAntrian) return;
    let batal = false;
    ambilTiketSaya().then((st) => { if (!batal) setStatusAntrian(st); });
    return () => { batal = true; };
  }, [bolehAntrian]);

  // G2 — dropdown pelanggan hanya untuk B2B/USER (yang diminta brief). Ini
  // dihitung dari `form`, jadi ia hidup/mati mengikuti pilihan Jenis Pelanggan
  // tanpa perlu state kedua yang bisa basi.
  const perluDropdownPelanggan = ["b2b", "user"].includes(form.jenis_pelanggan || "umum");
  // B2 (brief 22 Sep): pasangan nama + No HP yang BENAR-BENAR datang dari
  // kartu yang dipilih. Dibandingkan dengan isian sekarang (bukan sekadar
  // "pernah memilih"), jadi mengetik ulang salah satunya otomatis
  // membatalkan pilihan.
  // Pencarian kartu, "+ Pelanggan baru", dan gerbang izinnya hidup di SATU
  // komponen bersama Edit Penjualan (`components/pos/PilihPelangganKartu`).
  const kartuPelangganCocok = kartuCocok(form._kartu_pelanggan, form.nama_pelanggan, form.no_hp);
  // Dialog konfirmasi PPN sebelum simpan (brief bos) — menyimpan payload yang
  // SUDAH divalidasi/dibersihkan, menunggu klik "Lanjutkan". null = tidak tampil.
  const [ppnConfirm, setPpnConfirm] = useState(null);

  const [detail, setDetail] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [aiPanel, setAiPanel] = useState(false); // panel chat AI (hanya relevan saat modal terbuka)
  // PIN persetujuan hapus (brief #23) — retry() menyimpan aksi delete yang
  // sempat dibalas 428, dipanggil ulang begitu PIN dikirim.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}

  // Employees + payment methods (loaded once)
  const [employees, setEmployees] = useState([]);
  // Penanda "permintaan karyawan sudah selesai" (berhasil ATAU gagal). Modal
  // dari ?kode_antrian menunggu ini supaya nama sales terisi — tapi tidak
  // boleh menunggu selamanya kalau daftarnya memang kosong/ditolak.
  const [employeesTermuat, setEmployeesTermuat] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);     // active-only (untuk dropdown form)
  const [kreditPelanggan, setKreditPelanggan] = useState(0);    // sisa kredit retur pelanggan ini
  // I1: kredit atas nama yang sama tapi TANPA No HP — terkunci sampai SPV
  // melengkapinya, jadi kasir diberi tahu jalan keluarnya, bukan dibiarkan
  // mengira pelanggannya tidak punya kredit.
  const [kreditTerkunci, setKreditTerkunci] = useState(0);
  const [paymentGroups, setPaymentGroups] = useState({});       // active-only grouped
  const [paymentGroupsAll, setPaymentGroupsAll] = useState({});
  const [methodsModal, setMethodsModal] = useState(false);

  // (Peta resep "kit" kontrak B4 — `kitResepMap` — DIHAPUS 24 Sep 2026: resep
  // bundling yang berlaku (`bundle_definitions`) tidak pernah punya medan
  // `mode`, jadi saringan `mode === "kit"` selalu kosong dan petanya mati sejak
  // fitur paket ZOA menggantikannya. Komponen paket kini sepenuhnya lewat
  // `useResepBundlingAktif` + `BundleKomponenPicker`, sama dengan Sales.jsx.)

  // Restrict nama_penjual choice — my_only vs all_employees
  const [penjualScope, setPenjualScope] = useState("mine"); // "mine" | "all"

  // Pengurutan tabel daftar nota (client-side, lihat components/ui/sort.jsx) —
  // urutan bawaan tetap seperti sebelumnya (backend sudah `.sort("created_at",
  // -1).limit(500)`, jadi sort di sini HANYA menata ulang 500 nota terbaru
  // yang sudah termuat, bukan seluruh data kalau nota lebih dari itu).
  const notaSort = useSortState(null, "asc");

  // Filter daftar nota (brief owner 29 Agu, poin 2-3). Semuanya dikirim ke
  // SERVER: daftarnya dipotong 500 baris, jadi menyaring di layar akan
  // melewatkan nota lama tanpa satu pun tanda.
  const [fDari, setFDari] = useState("");
  const [fSampai, setFSampai] = useState("");
  const [fKategori, setFKategori] = useState("");
  const [fCabang, setFCabang] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [opsiCabang, setOpsiCabang] = useState([]);
  const [opsiBrand, setOpsiBrand] = useState([]);

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (showMine) p.set("mine", "true");
    if (fDari) p.set("dari", fDari);
    if (fSampai) p.set("sampai", fSampai);
    if (fKategori) p.set("kategori", fKategori);
    if (fCabang) p.set("branch", fCabang);
    if (fBrand) p.set("brand", fBrand);
    api.get(`/nota/sales?${p}`)
      .then(r => setItems(r.data || []))
      .catch(e => {
        const pesan = e?.response?.data?.detail;
        toast.error(e?.response?.status === 400 && pesan ? pesan : "Gagal memuat nota");
      });
    api.get("/nota/sales/summary/stats").then(r => setSummary(r.data || {}));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [status, showMine, fDari, fSampai, fKategori, fCabang, fBrand]);

  useEffect(() => {
    api.get("/finance/branches/master")
      .then(r => setOpsiCabang((r.data?.items || []).map(b => b.name || b)))
      .catch(() => { /* dropdown kosong; filter cabang tinggal tidak terpakai */ });
    api.get("/inventory/master-opsi")
      .then(r => {
        const m = r.data?.master_opsi || r.data || {};
        const merek = [...(m.brand_gadget || []), ...(m.brand_accessories || []), ...(m.brand || [])];
        setOpsiBrand([...new Set(merek)].sort());
      })
      .catch(() => { /* merek bebas diketik lewat kolom teks di bawah */ });
  }, []);

  useEffect(() => {
    api.get("/employees")
      .then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => { /* nama sales tinggal diketik manual seperti sebelumnya */ })
      .finally(() => setEmployeesTermuat(true));
    loadMethods();
  }, []);

  // Sisa kredit retur milik pelanggan yang sedang diketik. Ditunda 500ms supaya
  // tidak memanggil server tiap huruf; nama kosong langsung dinolkan tanpa
  // memanggil apa pun.
  //
  // I1 (brief owner 22 Sep 2026): kredit hanya milik pelanggan dengan nama
  // DAN No HP yang sama — jadi saldonya baru ditampilkan kalau KEDUANYA terisi
  // dan cocok. Versi lama mencari berdasarkan nama saja: orang lain yang
  // namanya sama melihat (dan bisa memakai) kredit pelanggan lain.
  useEffect(() => {
    const nama = (form.nama_pelanggan || "").trim();
    const hp = (form.no_hp || "").trim();
    if (!nama || !hp) { setKreditPelanggan(0); setKreditTerkunci(0); return; }
    let batal = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/retur/kredit-pelanggan", { params: { nama, no_hp: hp } });
        if (!batal) {
          setKreditPelanggan(r.data?.total_sisa || 0);
          setKreditTerkunci(r.data?.total_sisa_terkunci_tanpa_hp || 0);
        }
      } catch {
        // Kasir tanpa izin `retur.view` tetap harus bisa membuat nota —
        // penandanya cuma tidak muncul.
        if (!batal) { setKreditPelanggan(0); setKreditTerkunci(0); }
      }
    }, 500);
    return () => { batal = true; clearTimeout(t); };
  }, [form.nama_pelanggan, form.no_hp]);

  const loadMethods = () => {
    // Active-only untuk dropdown pemilihan di form
    api.get("/payment-methods?active_only=true").then(r => {
      setPaymentMethods(r.data?.items || []);
      setPaymentGroups(r.data?.groups || {});
    });
    // Full list (termasuk inactive) untuk Manager modal — inactive rows tampil greyed
    api.get("/payment-methods?active_only=false").then(r => {
      setPaymentGroupsAll(r.data?.groups || {});
    });
  };

  // Paket/bundling (poin 8-9, brief bos 23 Agu 2026, aturan #3 CLAUDE.md) —
  // alasan (kalau ada) kenapa tombol "Kirim ke Kasir"/"Simpan Perubahan"
  // masih ditahan. null = semua baris paket (kalau ada) sudah siap dikirim.
  const bundleAlasanTertahan = useMemo(
    () => cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache),
    [form.items, resepBundlingMap, produkInfoCache]
  );
  // Tampilan saja (bukan angka tagihan): jumlah baris barang sebelum diskon.
  const totalHarga = useMemo(() => form.items.reduce((s, i) => s + (parseFloat(i.harga) || 0), 0), [form.items]);
  const diskonBarisForm = useMemo(() => jumlahDiskonBaris(form.items), [form.items]);
  // Draf LAMA yang masih membawa diskon nota / biaya admin tersimpan (D2/D4) —
  // ditampilkan baca-saja dan dikirim ulang apa adanya.
  const diskonNotaLama = Number(form.diskon) || 0;
  const adminLama = !!form.biaya_admin_mode || (Number(form.biaya_admin_nilai) || 0) > 0;
  // DP pelanggan (brief owner 29 Agu, diperbaiki 12 Sep). Dicari lewat NOMOR
  // HP **atau** NAMA: owner meminta pengguna "memilih nama pelanggan dari
  // daftar DP yang tersedia", dan nomor HP saja tidak cukup — DP kerap disetor
  // dengan nomor yang ditulis beda format, atau tanpa nomor sama sekali.
  // Dimuat setelah jeda, bukan setiap ketikan, supaya tidak menembak server
  // tiap huruf. (Pemiliknya tetap diperiksa server lewat No HP — D8.)
  const [dpTersedia, setDpTersedia] = useState([]);
  const [dpDicari, setDpDicari] = useState(false);
  // Pelanggan (HP|nama) yang menghasilkan `dpTersedia` saat ini. Tanpa penanda
  // ini, membuka Edit draf ber-DP menilai DP-nya terhadap hasil pencarian
  // pelanggan SEBELUMNYA (atau daftar kosong awal) dan membuangnya diam-diam
  // sebelum pencarian untuk pelanggan draf itu sempat selesai.
  const dpKunci = `${(form.no_hp || "").trim()}|${(form.nama_pelanggan || "").trim()}`;
  const [dpKunciHasil, setDpKunciHasil] = useState(null);
  useEffect(() => {
    const hp = (form.no_hp || "").trim();
    const nama = (form.nama_pelanggan || "").trim();
    const kunci = `${hp}|${nama}`;
    // Ambang berbeda: nomor HP baru bermakna setelah 6 digit, nama setelah 3
    // huruf. Di bawah itu daftarnya akan berisi pelanggan orang lain.
    const pakaiHp = hp.length >= 6;
    const pakaiNama = nama.length >= 3;
    if (!pakaiHp && !pakaiNama) {
      setDpTersedia([]); setDpDicari(false); setDpKunciHasil(kunci); return;
    }
    let batal = false;
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (pakaiHp) params.no_hp = hp;
        if (pakaiNama) params.nama = nama;
        const r = await api.get("/down-payment/tersedia", { params });
        if (!batal) { setDpTersedia(r.data?.items || []); setDpDicari(true); setDpKunciHasil(kunci); }
      } catch { if (!batal) { setDpTersedia([]); setDpDicari(true); setDpKunciHasil(kunci); } }
    }, 400);
    return () => { batal = true; clearTimeout(t); };
  }, [form.no_hp, form.nama_pelanggan]);

  // Tiga angka yang diminta owner: saldo DP, nominal yang dipakai, sisa DP.
  // "Dipakai" datang dari PRATINJAU SERVER (di bawah) — server membatasi DP ke
  // tagihan − trade-in; rumus layar lama membatasinya ke total bruto dan bisa
  // menyarankan DP yang lebih besar dari yang masih harus dibayar.
  // D8: hanya DP ber-No HP SAMA dengan nota yang boleh jadi pilihan — server
  // menolak yang lain. Pencarian memang ikut mencocokkan awalan NAMA, jadi DP
  // "Budi Santoso" milik nomor lain bisa ikut terbawa; ia cukup dihitung
  // (`dpLainHp`) untuk keterangan, tanpa nomornya dan tanpa jadi opsi.
  const { cocok: dpCocok, lainHp: dpLainHp } = useMemo(
    () => pisahDpMenurutHp(dpTersedia, form.no_hp), [dpTersedia, form.no_hp]);
  const dpTerpilih = useMemo(
    () => (form.dp_id ? dpCocok.find(x => x.id === form.dp_id) || null : null),
    [form.dp_id, dpCocok],
  );
  const dpSaldo = dpTerpilih ? Number(dpTerpilih.sisa) || 0 : 0;
  // DP yang sudah tidak ada di daftar hasil pencarian TIDAK boleh tetap
  // terpilih. Mengganti nama/nomor pelanggan setelah memilih DP membuat
  // daftarnya berubah, tapi `form.dp_id` tidak pernah dibersihkan — layarnya
  // bahkan menulis "Tidak ada DP tersisa…" sementara payload-nya masih
  // membawa dp_id lama dan (karena `dp_nominal` kosong = seluruh sisa)
  // menghabiskan uang muka pelanggan yang SALAH. Dinilai HANYA terhadap hasil
  // pencarian untuk pelanggan yang sekarang (`dpKunciHasil === dpKunci`).
  // Dinilai terhadap `dpCocok` (No HP sama), bukan seluruh hasil pencarian:
  // mengganti No HP nota ke nomor lain juga harus melepas DP yang sudah dipilih.
  useEffect(() => {
    if (dpKunciHasil !== dpKunci) return;
    if (form.dp_id && !dpCocok.some(d => d.id === form.dp_id)) {
      setForm(f => ({ ...f, dp_id: "", dp_nominal: "" }));
    }
  }, [dpCocok, form.dp_id, dpKunciHasil, dpKunci]);

  // ---- Tagihan hitungan SERVER (brief 22 Sep D1/D4-D7) ----
  // Payload simpan dan badan pratinjau dirakit dari SATU fungsi
  // (`lib/notaPenjualan.js`), jadi angka yang dilihat kasir adalah angka
  // untuk payload yang akan terkirim. Rumus uangnya milik server
  // (`POST /nota/sales/pratinjau` → `routers/sales.py::rincian_tagihan`):
  // layar lama membulatkan PPN sendiri dan menolak kasir yang benar karena
  // selisih satu rupiah, lalu selisih yang lolos hanya ditanya lewat confirm().
  const payloadForm = useMemo(() => susunPayloadNota(form, { fmtRp: idr }), [form]);
  const kunciPratinjau = useMemo(() => JSON.stringify(bodyPratinjauNota(payloadForm)), [payloadForm]);
  const [pratinjau, setPratinjau] = useState({ kunci: null, data: null, gagal: null });
  useEffect(() => {
    if (!modal) return;
    let batal = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/nota/sales/pratinjau", JSON.parse(kunciPratinjau));
        if (!batal) setPratinjau({ kunci: kunciPratinjau, data, gagal: null });
      } catch (e) {
        if (!batal) {
          setPratinjau({ kunci: kunciPratinjau, data: null,
                         gagal: pesanGagalDariServer(e, "Gagal menghitung tagihan — periksa koneksi") });
        }
      }
    }, 350);
    return () => { batal = true; clearTimeout(t); };
  }, [modal, kunciPratinjau]);
  // Hasil pratinjau hanya dipercaya kalau dihitung untuk isi form YANG SEKARANG
  // — ketikan terakhir kasir bisa belum sempat dihitung ulang.
  const pratinjauSegar = pratinjau.kunci === kunciPratinjau && !!pratinjau.data;
  const rincian = pratinjauSegar ? pratinjau.data.rincian : null;
  const jumlahBaris = pratinjauSegar ? (pratinjau.data.jumlah_baris ?? 0) : null;
  const pesanPratinjau = pratinjauSegar ? (pratinjau.data.pesan || []) : [];
  const tagihanCocok = pratinjauSegar && !!pratinjau.data.cocok;
  const notaBertempo = !!(form.tempo_pembayaran || "").trim();
  // Total Pembayaran ketikan kasir (D1/D6): kosong = belum diisi (diisi di
  // dialog Setujui); terisi wajib = jumlah baris pembayaran.
  const totalKasirTerisi = selisihTotalKasir(form.total_pembayaran_kasir, 0) !== null;
  const totalKasirBeda = canApprove && totalKasirTerisi && jumlahBaris !== null
    && !totalKasirCocok(form.total_pembayaran_kasir, jumlahBaris);
  // Kenapa tombol simpan ditahan karena UANG. `menghitungTagihan` (pratinjau
  // belum kembali untuk ketikan terakhir) menahan tombol tanpa memunculkan
  // kotak peringatan — kalau tidak, kotaknya berkedip di setiap ketikan.
  const gagalPratinjauSekarang = pratinjau.kunci === kunciPratinjau && !!pratinjau.gagal;
  const menghitungTagihan = !pratinjauSegar && !gagalPratinjauSekarang;
  const alasanUangTertahan = menghitungTagihan
    ? "Menghitung tagihan…"
    : gagalPratinjauSekarang ? pratinjau.gagal
    : !tagihanCocok ? (pesanPratinjau[0] || "Jumlah pembayaran belum sesuai dengan yang harus dibayar.")
    : totalKasirBeda ? "Total Pembayaran tidak sama dengan jumlah pembayaran di nota — nota tidak bisa diproses."
    : null;

  // Live clock — updates form.waktu every minute so it always reflects server time
  useEffect(() => {
    if (!modal || modalMode !== "create") return;
    const tick = () => setForm(f => ({ ...f, waktu: new Date().toTimeString().slice(0, 5) }));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [modal, modalMode]);

  const availableEmployees = useMemo(() => {
    if (penjualScope === "mine") {
      // Prefer email match (more robust). Fallback to name.
      const byEmail = employees.filter(e => user?.email && e.email === user.email);
      if (byEmail.length) return byEmail;
      return employees.filter(e => e.nama === user?.name);
    }
    return employees;
  }, [penjualScope, employees, user]);

  const bukaModalCreate = (kode) => {
    // Default nama_penjual = user's own employee record (match by email first, then name)
    const me = employees.find(e => e.email === user?.email) || employees.find(e => e.nama === user?.name);
    // Cabang bawaan = cabang karyawan, TAPI hanya kalau cabang itu sah untuk
    // nota (D9). Cabang karyawan yang tidak punya Kas Toko + penampung dulu
    // lolos diam-diam; kini kasir memilih sendiri dari daftar yang sah.
    const cabangSaya = (me?.branch || "").trim();
    const cabangSah = BRANCHES.find(b => b.toLowerCase() === cabangSaya.toLowerCase()) || "";
    setForm({
      ...emptyForm(),
      nama_penjual: me?.nama || user?.name || "",
      branch: cabangSah,
      // Kode WAJIB diisi di sini, bukan sebelum fungsi ini dipanggil —
      // `...emptyForm()` di atas menghapus apa pun yang diset lebih dulu.
      kode_antrian: kode || "",
      // Pilihan dari meja inventory tetap melewati validasi nota dan antrian.
      ...(unitAwal ? {
        warehouse_id: unitAwal.warehouse_id,
        items: [{ ...emptyForm().items[0], sku: unitAwal.sku,
          nama_barang: unitAwal.nama || unitAwal.label, imei: unitAwal.imei,
          harga: unitAwal.harga_jual || 0,
          kode: deriveKode(unitAwal.nama || unitAwal.label),
          kategori: unitAwal.kategori || deriveKategoriTeksBebas(unitAwal.nama || unitAwal.label) }],
      } : {}),
    });
    setScanInline(false);
    setPesanTolakAntrian("");
    setKodeInline("");
    setPenjualScope("mine");
    setModalMode("create");
    setModal(true);
  };

  /** Buka form nota baru. `opts.kode` = kode tiket antrian yang sudah pasti
   *  (dari URL / konfirmasi); tanpa itu, pengguna wajib-scan ditanya dulu
   *  tiket mana yang dipakai. Status diambil SAAT KLIK, bukan dari hasil mount:
   *  FL bisa baru saja menutup tiket di tab lain. */
  const openCreate = async (opts) => {
    const kode = typeof opts?.kode === "string" ? opts.kode.trim() : "";
    if (kode) { bukaModalCreate(kode); return; }
    if (!bolehAntrian) { bukaModalCreate(""); return; }
    setMemeriksaAntrian(true);
    const st = await ambilTiketSaya();
    setMemeriksaAntrian(false);
    if (st) setStatusAntrian(st);
    if (!st?.wajib_scan) { bukaModalCreate(""); return; }
    const pilihan = daftarTiketPilihan(st);
    if (pilihan.length) {
      setKonfirmasiTiket({ pilihan, kode: pilihan[0].kode_antrian });
      return;
    }
    toast.error("Scan nomor antrian pelanggan dulu");
    navigate("/antrian?tab=layani");
  };

  // Datang dari Antrian (tombol "Membeli" / "Buat nota"): buka form berkode
  // SESUDAH daftar karyawan termuat — openCreate mengisi nama sales dari
  // daftar itu — lalu buang parameternya supaya muat ulang halaman tidak
  // membuka form yang sama lagi.
  useEffect(() => {
    // Parameter sudah dibuang = siap menerima kode BARU (mis. kembali lewat
    // tombol "Buat nota" tanpa halaman ini sempat di-unmount).
    if (!kodeDariUrl) { sudahBukaDariUrl.current = false; return; }
    if (sudahBukaDariUrl.current || !employeesTermuat) return;
    sudahBukaDariUrl.current = true;
    openCreate({ kode: kodeDariUrl });
    setSearchParams(p => { p.delete("kode_antrian"); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kodeDariUrl, employeesTermuat]);

  const pilihTiketKonfirmasi = () => {
    const kode = konfirmasiTiket?.kode;
    setKonfirmasiTiket(null);
    if (kode) bukaModalCreate(kode);
  };

  /** Panggil tiket dari DALAM modal setelah server menolak nota (409). Isian
   *  form tidak boleh hilang hanya karena tiketnya belum dipindai. */
  const panggilTiketInline = async (kodeMentah) => {
    const bersih = (kodeMentah || "").trim();
    if (!bersih) { toast.error("Kode antrian kosong"); return; }
    setBusyPanggil(true);
    try {
      const { data } = await api.post("/antrian/panggil", { kode_antrian: bersih });
      const kode = data?.item?.kode_antrian || bersih;
      setForm(f => ({ ...f, kode_antrian: kode }));
      setStatusAntrian(s => (s ? { ...s, tiket: data?.item || s.tiket } : s));
      setScanInline(false);
      setPesanTolakAntrian("");
      setKodeInline("");
      toast.success(`${data?.message || "Tiket terkunci"} — tekan Kirim ke Kasir lagi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memanggil tiket");
    } finally {
      setBusyPanggil(false);
    }
  };

  const openEdit = (n) => {
    setForm({
      // `...emptyForm()` WAJIB di depan. Tanpa itu, setiap medan yang
      // ditambahkan ke formulir SESUDAH fungsi ini ditulis akan bernilai
      // undefined saat nota dibuka — lalu PATCH /nota/sales mengganti dokumen
      // secara UTUH, jadi medan itu terhapus dari nota tanpa satu pun pesan.
      // Sudah menggigit: mengedit nota bertempo membuang jenis_pelanggan,
      // tempo_pembayaran, dan pic_approval_piutang — notanya diam-diam
      // berubah jadi Pelanggan Umum dan piutangnya tidak pernah lahir.
      ...emptyForm(),
      id: n.id,
      tanggal: n.tanggal, waktu: n.waktu,
      nama_pelanggan: n.nama_pelanggan || "", no_hp: n.no_hp || "",
      // Draf B2B/USER yang sudah tersimpan dianggap sudah memilih kartu;
      // kalau pasangannya ternyata tidak cocok kartu mana pun (draf lama
      // ketik-bebas), server menolaknya dengan pesan yang menyebut kartunya.
      _kartu_pelanggan: ["b2b", "user"].includes(n.jenis_pelanggan) && n.nama_pelanggan && n.no_hp
        ? { nama: n.nama_pelanggan, no_hp: n.no_hp } : null,
      // Nota dari SEBELUM field kategori ada (bukan alias lama — itu kunci
      // yang sah apa adanya): tebak sekali dari teks sebagai cadangan
      // terakhir supaya dropdown tidak kosong.
      items: (n.items?.length ? n.items : [{ nama_barang: "", harga: 0, kode: "HP" }])
        // Baris SINTETIK per komponen paket cuma pernah lahir di dokumen
        // `sales` (approve_nota_sale -> `ekspansi_komponen_bundle_untuk_
        // penjualan`), BUKAN di draf `nota_sales` yang halaman ini edit — jadi
        // secara normal `is_bundle_component` tidak pernah muncul di sini.
        // Filter tetap dipasang sebagai jaring pengaman (pola sama Sales.jsx)
        // supaya kalau suatu saat sumbernya berubah, baris gratis nyasar itu
        // tidak pernah tampil sebagai baris tersunting.
        .filter(it => !it.is_bundle_component)
        .map(it => ({
          line_id: it.line_id, addition: it.addition, box: it.box, qty: it.qty || 1,
          nama_barang: it.nama_barang || "", harga: it.harga || 0, kode: it.kode || "HP",
          // Diskon produk per baris (D5) — draf lama tanpa medan ini = 0.
          diskon: it.diskon || 0,
          kategori: it.kategori || deriveKategoriTeksBebas(it.nama_barang || ""),
          // SKU yang sudah tersimpan WAJIB ikut termuat — kalau tidak, edit nota
          // lama akan menghapus tautan katalognya begitu disimpan ulang.
          sku: it.sku || "",
          // IMEI per baris (brief bos poin 4) — nota lama tanpa field ini
          // (dibuat sebelum fitur ada) memuat "" seperti biasa, tetap bisa
          // dibuka/disimpan ulang tanpa IMEI (kompatibel mundur).
          imei: it.imei || "",
          // Penanda barang bonus (backend menolak harga 0 tanpa penanda ini) —
          // nota lama tanpa field ini dianggap bukan bonus (default false).
          is_bonus: !!it.is_bonus,
          // Baris non-stok eksplisit (kontrak G1 keputusan owner #2) — nota
          // lama (sebelum field ini ada) dianggap bukan non-stok; kalau
          // barisnya memang tanpa SKU, sales wajib menandainya ulang sebelum
          // bisa disimpan lagi (aturan baru, bukan pelonggaran data lama).
          non_stok: !!it.non_stok,
          // Snapshot komponen paket (poin 8) — dimuat APA ADANYA, dinormalkan
          // ke bentuk minimal kontrak KomponenBundleItem (lihat komentar sama
          // di Sales.jsx openEdit).
          komponen: Array.isArray(it.komponen) && it.komponen.length
            ? it.komponen.map(k => ({ sku: k.sku, qty: k.qty, imei: k.imei || null }))
            : null,
        })),
      imei: n.imei || "", metode_pembayaran: n.metode_pembayaran || "Cash",
      // Baris pembayaran format baru (D4) — `keterangan` ikut termuat supaya
      // No. Kontrak tidak hilang saat draf disimpan ulang. Draf LAMA tanpa
      // baris (satu metode) dibuka dengan metodenya sebagai baris utama
      // bernominal kosong: format lama tidak bisa disimpan lagi, jadi kasir
      // mengisi nominalnya (tombol "Isi sisa" di baris utama).
      payments: n.payments?.length
        ? n.payments.map(p => ({ method: p.method || "Cash", amount: p.amount || 0,
                                 keterangan: p.keterangan || "" }))
        : [barisPembayaranBaru(n.metode_pembayaran || "Cash")],
      // Trade-in "dipakai" = ada ISI unitnya (E15), bukan cuma benderanya.
      trade_in: !!n.trade_in || !!(n.trade_in_items || []).length, trade_in_note: n.trade_in_note || "",
      // Support both new (trade_in_items[]) and legacy (trade_in_note string) formats
      trade_in_items: n.trade_in_items?.length
        ? n.trade_in_items.map(t => ({
            nama: t.nama || "", status: t.status || "", warna: t.warna || "",
            bh: t.bh || "", kelengkapan: t.kelengkapan || "",
            imei: t.imei || "", catatan: t.catatan || "", harga: t.harga || 0 }))
        : (n.trade_in && n.trade_in_note
            ? [{ nama: n.trade_in_note, status: "", warna: "", bh: "", kelengkapan: "",
                 imei: "", catatan: "", harga: 0 }]
            : [{ nama: "", status: "", warna: "", bh: "", kelengkapan: "",
                 imei: "", catatan: "", harga: 0 }]),
      catatan: n.catatan || "",
      nama_penjual: n.nama_penjual || "", branch: n.branch || "",
      // Isian "Total Pembayaran" kasir TIDAK dimuat: server menghapusnya setiap
      // draf diedit (D1 — angka yang dikonfirmasi kasir bisa sudah bukan angka
      // nota ini lagi). Kasir mengetik ulang di sini atau di dialog Setujui.
      total_pembayaran_kasir: "",
      // Nota lama (sebelum fitur PPN ada) tidak punya field ini -> default
      // false, kompatibel mundur.
      kena_ppn: !!n.kena_ppn,
      // Nota lama (sebelum fitur Sumber Info ada, 11 Agu 2026) tidak punya
      // field ini -> "" (kosong), backend TIDAK memaksa diisi ulang kalau
      // PATCH ini sama sekali tidak menyentuhnya (lihat komentar di
      // routers/sales.py update_sale). Kalau kasir memang mengosongkannya di
      // sini, itu perubahan eksplisit dan tunduk saklar darurat seperti biasa.
      source_info: n.source_info || "", source_ref_id: n.source_ref_id || "",
      // Nota lama (sebelum kontrak G1) tidak punya field ini -> "" (kosong),
      // backend menurunkan gudangnya sendiri seperti sebelum fitur ini ada.
      warehouse_id: n.warehouse_id || "",
      // Medan brief 29 Agu yang sebelumnya hilang saat edit. Semua pakai
      // fallback ke bawaan emptyForm() supaya nota LAMA (yang memang tidak
      // punya medan ini) terbuka persis seperti dulu.
      jenis_pelanggan: n.jenis_pelanggan || "umum",
      tempo_pembayaran: n.tempo_pembayaran || "",
      pic_approval_piutang: n.pic_approval_piutang || "",
      diskon: n.diskon || 0,
      diskon_pic_approval: n.diskon_pic_approval || "",
      biaya_admin_mode: n.biaya_admin_mode || "",
      biaya_admin_nilai: n.biaya_admin_nilai || 0,
      dp_id: n.dp_id || "",
      dp_nominal: n.dp_nominal ?? "",
      refund_nama_bank: n.refund_nama_bank || "",
      refund_no_rekening: n.refund_no_rekening || "",
      refund_atas_nama: n.refund_atas_nama || "",
      // Hanya untuk spanduk baca-saja: backend mengabaikan kode_antrian di
      // PATCH, tapi menyalinnya tetap membuat payload membawa nilai aslinya.
      kode_antrian: n.kode_antrian || "",
    });
    setScanInline(false);
    setPesanTolakAntrian("");
    // If penjual not in "mine" scope, auto-switch to "all"
    const inMine = employees.some(e => (e.nama === user?.name || e.email === user?.email) && e.nama === n.nama_penjual);
    setPenjualScope(inMine ? "mine" : "all");
    setModalMode("edit");
    setDetail(null);
    setModal(true);
  };

  const submit = async () => {
    if (!form.nama_pelanggan.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    // B2: cermin aturan server (`pelanggan.kanonikal_pelanggan_nota`) supaya
    // kasir dicegat di sini dengan jalan keluarnya, bukan menabrak 400.
    if (perluDropdownPelanggan && !kartuPelangganCocok) {
      toast.error("Pelanggan B2B/USER wajib dipilih dari daftar — atau daftarkan lewat \"+ Pelanggan baru\".");
      return;
    }
    // Cabang & No HP WAJIB (brief 22 Sep D1/D8/D9) — cermin
    // `branches_master.pastikan_cabang_nota` & `pelanggan.hp_wajib_nota`.
    if (!(form.branch || "").trim()) { toast.error("Cabang wajib dipilih"); return; }
    if (BRANCHES.length && !BRANCHES.some(b => b.toLowerCase() === form.branch.trim().toLowerCase())) {
      toast.error(`Cabang "${form.branch}" belum terdaftar lengkap (Kas Toko + penampung trade-in) — pilih cabang lain dari daftar.`);
      return;
    }
    if (digitHp(form.no_hp).length < MIN_DIGIT_HP) {
      toast.error(`No HP pelanggan wajib diisi, minimal ${MIN_DIGIT_HP} digit`); return;
    }
    if (!form.nama_penjual.trim()) { toast.error("Nama sales wajib diisi"); return; }
    if (!form.items[0].nama_barang.trim()) { toast.error("Nama barang wajib diisi"); return; }
    // Sumber Info (brief bos 11 Agu 2026) — cermin persis aturan backend
    // (_validasi_dan_lengkapi_source_info) supaya kasir dicegat DI SINI,
    // bukan menabrak 400 dari server setelah menekan "Kirim ke Kasir".
    const errSourceInfo = validasiSourceInfo(form.source_info, form.source_ref_id, sourceInfoCfg);
    if (errSourceInfo) { toast.error(errSourceInfo); return; }
    // Backend menolak baris berharga Rp 0 kecuali ditandai bonus (brief bos) —
    // dicegat di sini dulu supaya sales tidak menabrak error 400 dari server
    // tanpa tahu jalan keluarnya.
    const itemGratisTanpaTanda = form.items.find(x => x.nama_barang.trim() && (parseFloat(x.harga) || 0) === 0 && !x.is_bonus);
    if (itemGratisTanpaTanda) {
      toast.error(`Harga "${itemGratisTanpaTanda.nama_barang}" masih Rp0 — centang "Bonus/Gratis" kalau barang ini memang diberikan cuma-cuma, atau isi harga jualnya.`);
      return;
    }
    // Baris ketik-bebas tanpa SKU WAJIB ditandai eksplisit non-stok (kontrak
    // G1 keputusan owner #2) — cermin persis aturan backend
    // (create_nota_sale/update_nota_sale) supaya sales dicegat DI SINI dengan
    // penjelasan, bukan menabrak 400 generik.
    const itemTanpaSkuTanpaTanda = form.items.find(x => x.nama_barang.trim() && !x.sku && !x.non_stok);
    if (itemTanpaSkuTanpaTanda) {
      toast.error(`"${itemTanpaSkuTanpaTanda.nama_barang}" belum terhubung ke katalog stok — pilih barang dari hasil pencarian, atau centang "Non-stok (jasa/barang luar)".`);
      return;
    }
    // Satu nota = satu gudang (kontrak G1 keputusan owner #4) — kalau sales
    // berhak >1 gudang dan ada baris ber-SKU (yang memang mengikat ke stok
    // gudang tertentu), gudang wajib dipilih di sini. Nota non-stok murni
    // tidak pernah menyentuh stok jadi tidak butuh gudang.
    if (tampilkanPilihGudang && !form.warehouse_id && form.items.some(x => x.sku)) {
      toast.error("Pilih gudang untuk nota ini — ada barang ber-SKU yang mengikat ke stok gudang tertentu.");
      return;
    }
    // Baris paket kit wajib IMEI terisi untuk tiap komponen ber-IMEI (kontrak
    // B4) — backend juga menolak (400 "komponen X kosong"/IMEI tidak
    // tersedia), tapi mencegat di sini dulu supaya kasir tahu KOMPONEN mana
    // yang belum lengkap, bukan cuma menabrak error generik dari server.
    for (const it of form.items) {
      const belum = (it.komponen || []).find(k => k.has_imei && !(k.imei || "").trim());
      if (belum) {
        toast.error(`Pilih dulu IMEI/SN untuk komponen "${belum.nama || belum.sku}" pada paket "${it.nama_barang}" sebelum menyimpan.`);
        return;
      }
    }
    // Satu unit tidak boleh terpilih di dua baris/komponen sekaligus DALAM
    // SATU nota ini (backend TIDAK menegakkan ini — `_tolak_stok_kosong`
    // memvalidasi tiap baris/komponen independen, lihat catatan di
    // ItemImeiField). Ini pagar terakhir sebelum terkirim; pemilihnya sendiri
    // sudah menonaktifkan opsi yang sama di baris/komponen lain, jadi ini
    // seharusnya jarang tersentuh.
    const imeiTerpakai = [
      ...form.items.map(x => (x.imei || "").trim()),
      ...form.items.flatMap(x => (x.komponen || []).map(k => (k.imei || "").trim())),
    ].filter(Boolean);
    const imeiDobel = imeiTerpakai.find((v, idx) => imeiTerpakai.indexOf(v) !== idx);
    if (imeiDobel) {
      toast.error(`IMEI ${imeiDobel} dipilih di lebih dari satu baris/komponen — satu unit hanya boleh dijual sekali dalam satu nota.`);
      return;
    }
    // Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — aturan #3 CLAUDE.md:
    // jangan biarkan kasir menekan "Kirim ke Kasir" lalu menabrak 400 dari
    // backend. Tombol sudah ditahan (lihat `disabled` di bawah); ini jaring
    // pengaman terakhir untuk kasus race (mis. stok berubah tepat sebelum klik).
    const alasanBundle = cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache);
    if (alasanBundle) { toast.error(alasanBundle); return; }
    if (form.trade_in) {
      const validTradeItems = (form.trade_in_items || []).filter(t => t.nama.trim());
      if (validTradeItems.length === 0) { toast.error("Isi minimal 1 unit trade-in dengan nama"); return; }
      // Kolom wajib trade-in (brief owner 29 Agu, Input Nota poin 4). Daftarnya
      // SAMA persis dengan `validasi_trade_in_wajib` di backend — dicek di sini
      // hanya supaya pesannya menyebut kolom yang kosong, bukan sebagai
      // pengganti penjagaan server. BH sengaja tidak termasuk (brief: opsional).
      const wajibTI = [["Nama barang", "nama"], ["Status", "status"],
                       ["Warna", "warna"], ["Kelengkapan", "kelengkapan"],
                       ["IMEI/SN", "imei"]];
      for (let i = 0; i < validTradeItems.length; i++) {
        const kurang = wajibTI
          .filter(([, k]) => !String(validTradeItems[i][k] || "").trim())
          .map(([label]) => label);
        if (kurang.length) {
          toast.error(`Unit trade-in #${i + 1} — wajib diisi: ${kurang.join(", ")}`);
          return;
        }
      }
    }
    if (form.jenis_pelanggan === "user" && !(form.pic_approval_piutang || "").trim()) {
      toast.error("Jenis pelanggan USER wajib menyebut PIC Persetujuan Piutang"); return;
    }
    // Diskon PRODUK per baris (D5): 0..harga, satu PIC per nota. Diskon nota
    // lama TIDAK boleh bercampur dengan diskon baris (server menolak campuran:
    // dua sumber untuk satu angka diskon sudah pernah menggerakkan uang).
    const errDiskon = cekDiskonBaris(form.items, form.diskon_pic_approval);
    if (errDiskon) { toast.error(errDiskon); return; }
    if (diskonNotaLama > 0 && diskonBarisForm > 0) {
      toast.error("Draf lama ini masih membawa diskon nota — diskon per produk tidak bisa dipakai bersamaan. Buat nota baru untuk memakai diskon per produk.");
      return;
    }
    // Baris pembayaran bernominal wajib punya metode (akun jurnalnya
    // ditentukan metode; metode kosong dulu jatuh ke kas bawaan tanpa jejak).
    const barisTanpaMetode = (form.payments || []).findIndex(p => (Number(p.amount) || 0) > 0 && !(p.method || "").trim());
    if (barisTanpaMetode >= 0) {
      toast.error(`Baris pembayaran #${barisTanpaMetode + 1}: pilih metode pembayarannya`); return;
    }
    // Kecocokan uang (D1/D2/D4/D10) — MEMBLOKIR, bukan lagi confirm().
    // Angkanya dari pratinjau server untuk isi form yang SEKARANG; server
    // memeriksa ulang aturan yang sama saat draf disimpan DAN saat approve.
    if (!pratinjauSegar) {
      toast.error(pratinjau.gagal || "Tagihan masih dihitung — tunggu sebentar lalu tekan lagi.");
      return;
    }
    if (!tagihanCocok) {
      toast.error(pesanPratinjau[0] || "Jumlah pembayaran belum sesuai dengan yang harus dibayar.");
      return;
    }
    // Total Pembayaran kasir (D1/D6): kalau diisi, wajib sama dengan jumlah
    // baris pembayaran — beda = tidak bisa diproses (alert di kolomnya).
    if (totalKasirBeda) {
      toast.error(`Total Pembayaran ${idr(form.total_pembayaran_kasir)} tidak sama dengan jumlah pembayaran ${idr(jumlahBaris)}.`);
      return;
    }
    // Payload dirakit fungsi yang SAMA dengan badan pratinjau (lib/notaPenjualan.js).
    const payload = { ...payloadForm };
    // Hanya kasir yang boleh mengirim Total Pembayaran — kiriman orang lain
    // dibuang server; tidak dikirim sama sekali supaya maksudnya jelas.
    if (!canApprove) payload.total_pembayaran_kasir = null;
    // PPN (brief bos): dialog konfirmasi sebelum simpan — supaya kasir sadar
    // nota ini akan menambahkan PPN sebagai kewajiban pajak (2400 PPN
    // Keluaran) sebelum benar-benar terkirim, bukan baru sadar setelah dicetak.
    if (form.kena_ppn) {
      setPpnConfirm(payload);
      return;
    }
    await doSave(payload);
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      if (modalMode === "create") {
        await api.post("/nota/sales", payload);
        toast.success("Nota terkirim — menunggu approval kasir");
      } else {
        await api.patch(`/nota/sales/${form.id}`, payload);
        toast.success("Nota diperbarui");
      }
      setModal(false);
      setPpnConfirm(null);
      // FL wajib-scan bekerja dari layar Antrian: kembalikan ke sana supaya
      // pelanggan berikutnya langsung bisa dipindai.
      if (modalMode === "create" && payload.kode_antrian && wajibScan) {
        navigate("/antrian?tab=layani");
        return;
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (modalMode === "create" && e?.response?.status === 409 && bolehAntrian) {
        // Modal TETAP terbuka: isian panjang (barang, IMEI, trade-in) tidak
        // boleh hilang hanya karena tiketnya belum dipindai. Status dimuat
        // ulang karena 409 bisa juga berarti tiketnya sudah dilepas SPV.
        const pesan = typeof detail === "string" ? detail : "Nota ditolak aturan antrian";
        toast.error(pesan);
        setPpnConfirm(null);
        const st = await ambilTiketSaya();
        if (st) setStatusAntrian(st);
        // Panel scan HANYA untuk penolakan yang memang soal antrian. Pengguna
        // wajib-scan yang SUDAH membawa kode bisa kena 409 karena hal lain
        // (mis. IMEI unitnya baru saja terjual di nota lain) — menyodorkan panel
        // scan di situ menyuruhnya memindai ulang tiket yang tidak bermasalah,
        // sementara masalah sebenarnya tenggelam di bawah panel.
        const soalAntrian = !payload.kode_antrian || /antrian/i.test(pesan);
        if (st?.wajib_scan && soalAntrian) {
          setPesanTolakAntrian(pesan);
          setScanInline(true);
        }
      } else {
        // Kasus gudang buntu (400 "Pilih gudang..." tapi picker-nya sendiri
        // tidak tampil di form ini) diarahkan ke jalan keluar — lihat
        // docstring `pesanGudangButaArah` (lib/gudang.js).
        toast.error(pesanGudangButaArah(e, tampilkanPilihGudang) || pesanGagalDariServer(e, "Gagal simpan nota"));
      }
    }
    finally { setSaving(false); }
  };

  // Setujui nota (brief 22 Sep D1): KETIGA tombol Setujui (baris desktop,
  // kartu HP, dan detail) membuka dialog yang SAMA — kasir wajib mengetik
  // "Total Pembayaran" (uang yang benar-benar ia terima) dan server menolak
  // approve kalau kosong atau tidak sama dengan jumlah baris pembayaran nota.
  // Dulu approve satu klik tanpa isian dari tiga tempat berbeda.
  const [setujuiNota, setSetujuiNota] = useState(null);
  const bukaSetujui = (n) => setSetujuiNota(n);
  const setelahDisetujui = (data) => {
    const saleId = data?.sale_id;
    // Cetak dipindah ke Input Penjualan (brief bos 21 Agu 2026 #1) — toast
    // ini hanya mengonfirmasi transfer, tidak lagi menawarkan aksi cetak.
    toast.success(`✓ Nota disetujui & ditransfer ke Input Penjualan (ID: ${saleId?.slice(0, 8)}...)`);
    // Peringatan non-blokir "dijual di bawah modal" (brief bos 21 Agu #B1)
    // — nota adalah jalur kasir yang sesungguhnya, jadi tunduk kontrak yang
    // SAMA dengan Sales.jsx (lihat doSaveSale di sana): tidak menyebut
    // angka modal, hanya nama/SKU barangnya.
    (data?.sale?.peringatan_di_bawah_modal || []).forEach(p =>
      toast.warning(`${p.nama_barang || p.sku} dijual di bawah modal`, { duration: 8000 }));
    setSetujuiNota(null);
    setDetail(null);
    load();
  };

  const openReject = (n) => { setRejectModal(n); setRejectReason(""); };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan wajib diisi"); return; }
    try {
      await api.post(`/nota/sales/${rejectModal.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Nota ditolak");
      setRejectModal(null); setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reject"); }
  };

  const doDeleteNota = async (id, pin) => {
    await api.delete(`/nota/sales/${id}`, pin ? { params: { pin } } : undefined);
    toast.success("Nota dihapus");
    setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan pemegang izin
  // Pusat Approval). Ajukan otomatis, lalu buka dialog PIN.
  const deleteNota = async (n) => {
    if (!confirm(`Hapus nota ini?`)) return;
    try {
      await doDeleteNota(n.id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "nota_sales", target_id: n.id,
            ringkasan: `Hapus nota penjualan ${n.nama_pelanggan || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteNota(n.id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal hapus");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // Item helpers
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { nama_barang: "", harga: 0, diskon: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, non_stok: false, komponen: null }] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((row, j) => j !== i && row.addition?.parent_line_id !== f.items[i].line_id) }));
  const updItem = (i, k, v) => setForm(f => {
    const arr = [...f.items];
    arr[i] = { ...arr[i], [k]: (k === "harga" || k === "diskon") ? (parseFloat(v) || 0) : v };
    // Kategori ikut disarankan ulang HANYA sebagai cadangan teks bebas (baris
    // tanpa SKU) — baris ber-SKU tidak pernah lewat jalur ini, lihat
    // ItemCariField.pilih() yang memakai kategori master produk apa adanya.
    if (k === "nama_barang") { arr[i].kode = deriveKode(v); arr[i].kategori = deriveKategoriTeksBebas(v); }
    return { ...f, items: arr };
  });
  // Kumpulkan semua IMEI yang sudah terpilih di form ini (baris utama MAUPUN
  // komponen paket), supaya satu unit tidak bisa dipilih dua kali dalam satu
  // nota. `kecuali` menandai field yang sedang dirender supaya nilainya
  // sendiri tidak ikut memblokir dirinya (persis pola `imeiTerpakaiLain` lama,
  // diperluas mencakup komponen).
  const semuaImeiDipakai = ({ row, komponenIdx } = {}) => {
    const set = new Set();
    form.items.forEach((it, ri) => {
      const iniUtama = row === ri && komponenIdx === undefined;
      if (!iniUtama && (it.imei || "").trim()) set.add(it.imei.trim());
      (it.komponen || []).forEach((k, ci) => {
        const iniKomponen = row === ri && komponenIdx === ci;
        if (!iniKomponen && (k.imei || "").trim()) set.add(k.imei.trim());
      });
    });
    return set;
  };

  // Tempel beberapa field item sekaligus — dipakai autocomplete produk supaya
  // nama_barang, sku, harga, dan kategori terisi dalam satu update.
  const patchItem = (i, patch) => setForm(f => {
    const arr = [...f.items];
    // F3: produk paket yang dipilih mencatat harga katalognya sebagai acuan
    // kunci harga (lihat `hargaPaketTerkunci`, lib/notaPenjualan.js).
    arr[i] = { ...arr[i], ...patchBarisDenganKunciPaket(patch, resepBundlingMap) };
    return { ...f, items: arr };
  });

  // Kontrak G2 bagian C: memilih unit ber-IMEI dari ItemCariField sudah
  // menunjukkan gudang aslinya — satu nota = satu gudang (kontrak G1 #4),
  // jadi diisikan ke sini otomatis kalau kasir belum memilih gudang sendiri
  // (tidak pernah MENIMPA pilihan yang sudah dibuat kasir).
  const onWarehouseHint = (wid) => setForm(f => (!f.warehouse_id ? { ...f, warehouse_id: wid } : f));

  // Terima baris hasil urai AI → TAMBAHKAN ke items form yang sudah ada.
  // qty dipecah jadi baris terpisah karena form nota tidak punya kolom qty —
  // satu baris = satu barang, sesuai perhitungan total & laporan upselling.
  const applyAiItems = (rows) => {
    if (!rows?.length) return;
    setForm(f => {
      const tambahan = [];
      rows.forEach(r => {
        const n = Math.max(1, parseInt(r.qty) || 1);
        for (let i = 0; i < n; i++) {
          tambahan.push({
            nama_barang: r.nama_barang || "",
            harga: parseFloat(r.harga) || 0,
            kode: r.kode || deriveKode(r.nama_barang),
            // Hasil AI selalu tanpa SKU (lihat komentar `sku` di bawah) — cadangan
            // teks bebas yang sama dipakai baris ketik manual, TIDAK menebak
            // Tab/Laptop/Wearable dari teks.
            kategori: r.kategori || deriveKategoriTeksBebas(r.nama_barang),
            sku: "", // hasil AI tidak pernah pra-tertaut katalog — kasir bisa pilih lewat autocomplete
            imei: "", // idem — IMEI baris cuma bisa dipilih setelah SKU tertaut
            is_bonus: false, // hasil AI tidak pernah menandai bonus — kasir yang menegaskan manual
            // Baris hasil AI belum tertaut SKU (kontrak G1) -> non_stok
            // sengaja default false, BUKAN true: kasir tetap harus menegaskan
            // sendiri apakah ini barang stok yang belum ditautkan atau memang
            // jasa/barang luar, sebelum nota bisa disimpan.
            non_stok: false,
          });
        }
      });
      // Baris kosong bawaan dibuang supaya hasil AI tidak nyelip di bawah baris kosong
      const terisi = f.items.filter(it => (it.nama_barang || "").trim());
      const next = { ...f, items: [...terisi, ...tambahan] };
      // Field IMEI nota tunggal — isi hanya kalau masih kosong, jangan menimpa ketikan kasir
      const imeiAi = rows.map(r => (r.imei || "").trim()).filter(Boolean);
      if (!(f.imei || "").trim() && imeiAi.length) next.imei = imeiAi.join(", ");
      return next;
    });
    toast.success(`${rows.length} baris hasil AI dimasukkan ke form`);
  };

  // Metode "Trade-in" tidak ditawarkan di baris pembayaran nota (D5): server
  // menolaknya (`bersihkan_baris_pembayaran`) karena nilai unit trade-in sudah
  // dijurnal lewat bagian Trade-in — memilihnya hanya berujung 400.
  const paymentGroupsNota = useMemo(() => Object.fromEntries(
    Object.entries(paymentGroups || {})
      .map(([g, list]) => [g, (list || []).filter(m => !metodeTradeIn(m.name))])
      .filter(([, list]) => list.length)
  ), [paymentGroups]);
  const bayarUtama = (form.payments || [])[0] || barisPembayaranBaru("Cash");
  // Payment helpers (D4). Baris 0 = metode pembayaran UTAMA; baris 1.. =
  // "tambah pembayaran" (keterangan bebas | metode | nominal).
  const addPay = () => setForm(f => ({ ...f, payments: [...(f.payments || []), barisPembayaranBaru(paymentMethods[0]?.name || "Cash")] }));
  const rmPay = (i) => setForm(f => ({ ...f, payments: (f.payments || []).filter((_, j) => j !== i) }));
  const updPay = (i, k, v) => setForm(f => {
    const arr = [...(f.payments || [])];
    arr[i] = { ...arr[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v };
    return { ...f, payments: arr };
  });
  // Centang "Tambah pembayaran" menambah baris kedua; melepasnya membuang
  // baris tambahan (baris utama tetap). Cuma bentuk isian — angka yang harus
  // dibayar tetap dari server.
  const setTambahPembayaran = (aktif) => setForm(f => {
    const arr = [...(f.payments || [])];
    if (!arr.length) arr.push(barisPembayaranBaru("Cash"));
    if (aktif) return { ...f, payments: arr.length > 1 ? arr : [...arr, barisPembayaranBaru(paymentMethods[0]?.name || "Cash")] };
    return { ...f, payments: arr.slice(0, 1) };
  });
  // "Isi sisa" pada satu baris: nominalnya = yang masih harus dibayar menurut
  // SERVER dikurangi baris lain. Hanya mengisi kolom — kasir tetap melihat dan
  // menyimpan angkanya sendiri, dan server tetap memeriksa kecocokannya.
  const isiSisaPembayaran = (i) => {
    if (!rincian) { toast.info("Tagihan masih dihitung — coba lagi sebentar."); return; }
    const lain = (form.payments || []).reduce((s, p, j) => s + (j === i ? 0 : (Number(p.amount) || 0)), 0);
    updPay(i, "amount", Math.max(0, Math.round((rincian.harus_dibayar - lain) * 100) / 100));
  };

  // Trade-in item helpers (multi-unit)
  const addTradeItem = () => setForm(f => ({ ...f, trade_in_items: [...(f.trade_in_items || []),
    { nama: "", status: "", warna: "", bh: "", kelengkapan: "", imei: "", catatan: "", harga: 0 }] }));
  const rmTradeItem = (i) => setForm(f => ({ ...f, trade_in_items: (f.trade_in_items || []).filter((_, j) => j !== i) }));
  const updTradeItem = (i, k, v) => setForm(f => {
    const arr = [...(f.trade_in_items || [])];
    arr[i] = { ...arr[i], [k]: k === "harga" ? (parseFloat(v) || 0) : v };
    return { ...f, trade_in_items: arr };
  });
  // (Tombol "Auto-Fill Selisih" trade-in DIHAPUS, brief 22 Sep D5: ia menulis
  // NILAI UNIT trade-in dari selisih pembayaran — kebalikan dari urutan yang
  // benar. Nilai unit ditentukan penaksirnya; selisih pembayaran ditutup lewat
  // baris pembayaran, dan selisih yang tersisa kini MEMBLOKIR simpan.)

  // Pengaman isian belum tersimpan (brief owner) — form Buat/Edit Nota adalah
  // form terpanjang di halaman ini (item + trade-in + IMEI). Hook dipanggil di
  // level komponen (bukan di dalam JSX kondisional `{modal && ...}`), jadi
  // `active` WAJIB diikat ke flag buka/tutup modalnya sendiri supaya status
  // "dirty" & konfirmasi ikut reset tiap modal dibuka ulang (lihat docstring
  // `active` di hooks/useModalGuard.jsx).
  const notaGuard = useModalGuard({
    onClose: () => setModal(false), onSubmit: submit, busy: saving, dangerous: true, active: modal,
  });
  // Dialog PPN (hand-rolled, di luar cakupan useModalGuard — lihat komentar
  // di titik render-nya) terbuka DI ATAS modal Nota ini. Daftarkan sebagai
  // lapisan lebih atas supaya Escape yang ditujukan untuk dialog PPN tidak
  // ikut memicu palang "ada isian belum disimpan" milik notaGuard di
  // baliknya (lihat "LAPISAN PALING ATAS YANG MENANG" di useModalGuard.jsx).
  useModalLayer(!!ppnConfirm);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] bg-[#F2F3F6] md:bg-transparent min-h-screen md:min-h-0"
      data-testid="input-nota-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="hidden md:block text-[11px] font-semibold uppercase tracking-wider text-emerald-700">POS &amp; Sales</p>
          <h1 className="font-display text-xl md:text-3xl font-bold text-slate-900 md:mt-1">Input Nota Penjualan</h1>
          <p className="hidden md:block text-xs text-slate-500 mt-1">
            Sales input nota sementara → Kasir/Admin review &amp; approve → auto-transfer ke Input Penjualan.
          </p>
          {/* Cetak faktur/struk DIHAPUS dari halaman ini (brief bos 21 Agu 2026
              #1) — nota yang sudah disetujui otomatis jadi dokumen penjualan,
              jadi pencetakannya dipusatkan di satu tempat. Link disembunyikan
              kalau pengguna sendiri tidak punya izin membuka halaman itu. */}
          {bolehLihatPenjualan && (
            <p className="hidden md:block text-xs text-slate-500 mt-1">
              Cetak faktur/struk dilakukan di{" "}
              <Link to="/sales" data-testid="nota-link-cetak-penjualan"
                className="text-emerald-700 hover:underline font-medium">Input Penjualan</Link>{" "}
              setelah nota disetujui.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          {canManageMethods && (
            <TombolKelolaMetode jumlah={paymentMethods.length} onClick={() => setMethodsModal(true)} />
          )}
          {/* Dibungkus lambda: openCreate kini menerima opsi, dan event klik
              tidak boleh terbaca sebagai opsi. */}
          <button onClick={() => openCreate()} disabled={memeriksaAntrian} data-testid="btn-new-nota"
            className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 h-12 md:h-10 px-4 rounded-2xl md:rounded-lg bg-[#141922] md:bg-[#059669] hover:bg-[#22293a] md:hover:bg-[#047857] text-white text-[15px] md:text-sm font-semibold md:font-medium disabled:opacity-60">
            <Plus className="w-4 h-4" /> {memeriksaAntrian ? "Memeriksa antrian..." : "Buat Nota Baru"}
          </button>
        </div>
      </header>

      {wajibScan && (
        <div className="mb-4 flex flex-wrap items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="nota-antrian-wajib-spanduk">
          <Ticket className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1 min-w-0">
            Akun Anda wajib memindai nomor antrian pelanggan sebelum membuat nota.
          </span>
          <Link to="/antrian?tab=layani" data-testid="nota-antrian-wajib-link"
            className="font-medium text-amber-900 underline">
            Scan di Antrian
          </Link>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-5">
        <StatCard I={Clock} label="Menunggu" value={summary.pending || 0} tint="amber" />
        <StatCard I={CheckCircle2} label="Disetujui" value={summary.approved || 0} tint="emerald" />
        <StatCard I={XCircle} label="Ditolak" value={summary.rejected || 0} tint="rose" />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-slate-500">Status:</label>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="filter-status"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua</option>
          <option value="pending">Menunggu Approval</option>
          <option value="approved">Disetujui</option>
          <option value="rejected">Ditolak</option>
        </select>
        {canApprove && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-2">
            <input type="checkbox" checked={showMine} onChange={e => setShowMine(e.target.checked)}
              data-testid="filter-mine" className="w-3.5 h-3.5" />
            Hanya nota saya
          </label>
        )}
        {/* Periode, kategori, cabang, merek (brief owner 29 Agu poin 2-3) —
            semuanya disaring di SERVER. */}
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <label className="text-xs font-medium text-slate-500">Periode:</label>
        <input type="date" value={fDari} onChange={e => setFDari(e.target.value)}
          data-testid="nota-filter-dari"
          className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        <span className="text-xs text-slate-400">s/d</span>
        <input type="date" value={fSampai} onChange={e => setFSampai(e.target.value)}
          data-testid="nota-filter-sampai"
          className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        <select value={fKategori} onChange={e => setFKategori(e.target.value)}
          data-testid="nota-filter-kategori"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua kategori</option>
          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <select value={fCabang} onChange={e => setFCabang(e.target.value)}
          data-testid="nota-filter-cabang"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua cabang</option>
          {opsiCabang.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={fBrand} onChange={e => setFBrand(e.target.value)}
          data-testid="nota-filter-brand"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua merek</option>
          {opsiBrand.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {(fDari || fSampai || fKategori || fCabang || fBrand) && (
          <button onClick={() => { setFDari(""); setFSampai(""); setFKategori(""); setFCabang(""); setFBrand(""); }}
            data-testid="nota-filter-reset"
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
            Reset
          </button>
        )}
        <span className="ml-auto text-[11px] text-slate-500">{items.length} nota ditampilkan</span>
      </div>

      {/* List — tabel penuh di layar besar, kartu bertumpuk di HP (staf sales
          paling sering membuka daftar nota ini lewat ponsel). */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <TabelDesktop>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-tanggal" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-pelanggan" />
              <SortTh label="Sales" sortKeyName="nama_penjual" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-sales" />
              <SortTh label="Cabang" sortKeyName="branch" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-sort-cabang" />
              <SortTh label="Nilai Nota" sortKeyName="nilai_nota" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} align="right" testid="nota-sort-total" />
              <th className="text-center py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-400">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Belum ada nota. Klik <b>Buat Nota Baru</b> untuk mulai.
              </td></tr>
            )}
            {sortRows(items, notaSort.sortKey, notaSort.sortDir).map(n => {
              const meta = STATUS_META[n.status] || STATUS_META.pending;
              const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
              return (
                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`nota-row-${n.id}`}>
                  <td className="py-3 px-4">
                    <p className="text-xs text-slate-900 font-medium">{n.tanggal}</p>
                    <p className="text-[10px] text-slate-500">{n.waktu}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{n.nama_pelanggan}</p>
                    <p className="text-[10px] text-slate-500">{n.no_hp || "-"}</p>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-700">{n.nama_penjual}</td>
                  <td className="py-3 px-4 text-xs text-slate-600">{n.branch || "-"}</td>
                  <td className="py-3 px-4 text-right">
                    {/* B3: angka besar = NILAI NOTA (barang − diskon + admin + PPN)
                        hitungan server (`_tampilan_draf`), bukan total bruto. */}
                    <p className="font-mono font-semibold text-slate-900" data-testid={`nota-nilai-${n.id}`}>{idr(n.nilai_nota ?? n.total_harga)}</p>
                    {(n.diskon_tampil || 0) > 0 && (
                      <p className="text-[10px] text-rose-700">diskon −{idr(n.diskon_tampil)}</p>
                    )}
                    <p className="text-[10px] text-slate-500">{n.n_items} item{n.trade_in ? " · Trade-in" : ""}</p>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}
                      data-testid={`nota-status-${n.id}`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.status === "approved" && (
                      <p className="text-[10px] text-emerald-700 mt-0.5">by {n.approved_by_name || n.approved_by}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => setDetail(n)} title="Lihat detail" data-testid={`btn-detail-${n.id}`}
                      className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                      <Eye className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <button onClick={() => openEdit(n)} title="Edit nota" data-testid={`btn-edit-${n.id}`}
                        className="p-2 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canApprove && n.status === "pending" && (
                      <>
                        <button onClick={() => bukaSetujui(n)} title="Setujui" data-testid={`btn-approve-${n.id}`}
                          className="p-2 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => openReject(n)} title="Tolak" data-testid={`btn-reject-${n.id}`}
                          className="p-2 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                      <button onClick={() => deleteNota(n)} title="Hapus" data-testid={`btn-del-${n.id}`}
                        className="p-2 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="nota-kartu">
          {items.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-400">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Belum ada nota. Klik <b>Buat Nota Baru</b> untuk mulai.
            </p>
          ) : sortRows(items, notaSort.sortKey, notaSort.sortDir).map(n => {
            const meta = STATUS_META[n.status] || STATUS_META.pending;
            const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
            return (
              <KartuBaris key={n.id} testid={`nota-card-${n.id}`}>
                <KartuBarisHeader
                  kiri={<>
                    <p className="text-sm font-semibold text-slate-900">{n.nama_pelanggan}</p>
                    <p className="text-[11px] text-slate-500">{n.no_hp || "-"}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{n.tanggal} · {n.waktu}</p>
                  </>}
                  kanan={<>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}
                      data-testid={`nota-status-${n.id}-m`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.status === "approved" && (
                      <p className="text-[10px] text-emerald-700 mt-0.5">by {n.approved_by_name || n.approved_by}</p>
                    )}
                  </>}
                />
                <KartuBarisMeta label="Sales" value={n.nama_penjual} />
                <KartuBarisMeta label="Cabang" value={n.branch || "-"} />
                <KartuBarisMeta label={`${n.n_items} item${n.trade_in ? " · Trade-in" : ""}${(n.diskon_tampil || 0) > 0 ? ` · diskon ${idr(n.diskon_tampil)}` : ""}`}
                  value={idr(n.nilai_nota ?? n.total_harga)} tebal />
                <KartuBarisAksi>
                  <button onClick={() => setDetail(n)} title="Lihat detail" data-testid={`btn-detail-${n.id}-m`}
                    className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                    <Eye className="w-4 h-4" />
                  </button>
                  {canEdit && (
                    <button onClick={() => openEdit(n)} title="Edit nota" data-testid={`btn-edit-${n.id}-m`}
                      className="p-2 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {canApprove && n.status === "pending" && (
                    <>
                      <button onClick={() => bukaSetujui(n)} title="Setujui" data-testid={`btn-approve-${n.id}-m`}
                        className="p-2 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => openReject(n)} title="Tolak" data-testid={`btn-reject-${n.id}-m`}
                        className="p-2 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700">
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                    <button onClick={() => deleteNota(n)} title="Hapus" data-testid={`btn-del-${n.id}-m`}
                      className="p-2 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </KartuBarisAksi>
              </KartuBaris>
            );
          })}
        </DaftarKartuMobile>
      </div>

      {/* Modal: Create / Edit */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={notaGuard.requestClose}>
          <div ref={notaGuard.contentRef} className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="nota-modal">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-display font-semibold text-base">
                  {modalMode === "create" ? "Buat Nota Baru" : "Edit Nota"}
                </h3>
                <p className="text-[11px] text-emerald-100">
                  {modalMode === "create"
                    ? "Nota akan diteruskan ke kasir untuk direview."
                    : "Perubahan akan me-reset status jika sebelumnya ditolak."}
                </p>
              </div>
              <button onClick={notaGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              {form.kode_antrian && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                  data-testid="nota-antrian-spanduk">
                  <Ticket className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    {nomorTiketForm ? <>Tiket antrian <b>No. {nomorTiketForm}</b> · </> : "Tiket antrian · "}
                    <span className="font-mono text-xs break-all">{form.kode_antrian}</span>
                  </span>
                  {modalMode === "create" && bolehBukaAntrian && (
                    <Link to="/antrian?tab=layani" data-testid="nota-antrian-kembali"
                      className="text-xs font-medium text-emerald-800 underline">
                      Kembali ke Antrian
                    </Link>
                  )}
                </div>
              )}
              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Tanggal">
                  <DatePicker value={form.tanggal} onChange={v => setForm(f => ({ ...f, tanggal: v }))}
                    data-testid="input-tanggal" />
                </Field>
                <Field label={<span className="inline-flex items-center gap-1">Waktu <span className="text-[9px] px-1.5 py-0 rounded bg-emerald-100 text-emerald-800 font-bold">LIVE</span></span>}>
                  <input type="time" value={form.waktu} readOnly disabled
                    data-testid="input-waktu"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-600 cursor-not-allowed" />
                </Field>
                <Field label="Cabang *">
                  {/* D1/D9: wajib, dan HANYA cabang yang punya Kas Toko +
                      penampung trade-in. Cabang tersimpan draf LAMA yang sudah
                      tidak sah tetap terlihat (bertanda) supaya kasir tahu
                      kenapa harus memilih ulang — bukan hilang diam-diam. */}
                  <select value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
                    data-testid="input-cabang"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih cabang —</option>
                    {form.branch && !BRANCHES.some(b => b.toLowerCase() === form.branch.toLowerCase()) && (
                      <option value={form.branch} disabled>{form.branch} (belum terdaftar lengkap)</option>
                    )}
                    {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  {BRANCHES.length === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="input-cabang-kosong">
                      Belum ada cabang yang lengkap (Kas Toko + penampung trade-in). Daftarkan
                      di Master Data › Cabang.
                    </p>
                  )}
                </Field>
                {/* Satu nota = satu gudang (kontrak G1 keputusan owner #4) —
                    hanya muncul kalau sales berhak >1 gudang, lihat
                    docstring useGudangOptions. */}
                {tampilkanPilihGudang && (
                  <Field label="Gudang *">
                    <select value={form.warehouse_id}
                      onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))}
                      data-testid="input-warehouse"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                      <option value="">— pilih gudang —</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
                    </select>
                  </Field>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Jenis pelanggan & tempo (owner 29 Agu 2026). Tempo hanya
                    muncul untuk B2B/USER — pelanggan umum bayar di tempat, dan
                    backend menolaknya, jadi menampilkannya di sini cuma
                    melahirkan klik yang berujung 400.

                    B2 (brief 22 Sep): select ini DIPINDAH ke SEBELUM Nama
                    Pelanggan. Kasir yang mengetik nama dulu tidak pernah
                    melihat daftar pelanggan, karena daftarnya baru muncul
                    setelah jenisnya diganti ke B2B/USER. */}
                <Field label="Jenis Pelanggan">
                  <select value={form.jenis_pelanggan || "umum"} data-testid="input-jenis-pelanggan"
                    onChange={e => setForm(f => ({
                      ...f, jenis_pelanggan: e.target.value,
                      // Ganti ke umum = tempo & PIC dibersihkan, kalau tidak
                      // nilainya tertinggal dan dikirim diam-diam.
                      tempo_pembayaran: e.target.value === "umum" ? "" : f.tempo_pembayaran,
                      pic_approval_piutang: e.target.value === "user" ? f.pic_approval_piutang : "",
                    }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="umum">Pelanggan Umum</option>
                    <option value="b2b">B2B</option>
                    <option value="user">USER</option>
                  </select>
                </Field>
                <Field label="Nama Pelanggan *">
                  {/* B2 (brief owner 22 Sep 2026, mengulang G2 15 Sep): nama
                      pelanggan B2B/USER WAJIB dipilih dari daftar kartu
                      pelanggan. Alasan owner PENAGIHAN: surat "Tagih
                      Sekaligus" mengelompokkan piutang lewat TEKS nama
                      (`piutang.py::_kunci_nama_pelanggan`), jadi satu typo
                      memecah tagihan satu pelanggan jadi dua surat. G2 cuma
                      SARAN dan owner mengulang butirnya — saran tidak
                      mencegah apa pun.

                      Memilih kartu mengisi nama DAN No HP sekaligus; mengetik
                      ulang nama melepas pilihannya. Pelanggan yang belum punya
                      kartu didaftarkan lewat "+ Pelanggan baru" (nama + No HP
                      wajib) — tanpa pintu itu penjualan ke B2B baru mati
                      total. Server menegakkan aturan yang sama di semua jalur
                      tulis (`pelanggan.kanonikal_pelanggan_nota`). Pelanggan
                      umum tetap ketik bebas: ia tidak menerbitkan piutang. */}
                  {perluDropdownPelanggan ? (
                    <PilihPelangganKartu
                      nama={form.nama_pelanggan}
                      noHp={form.no_hp}
                      kartu={form._kartu_pelanggan}
                      onUbahNama={v => setForm(f => ({ ...f, nama_pelanggan: v, _kartu_pelanggan: null }))}
                      // No HP SELALU ikut kartu yang dipilih — nama dari kartu A
                      // dengan nomor B melahirkan pasangan yang ditolak server
                      // dan memecah kredit retur/DP pelanggan yang sama.
                      onPilih={k => setForm(f => ({ ...f, nama_pelanggan: k.nama, no_hp: k.no_hp,
                                                    _kartu_pelanggan: k }))}
                      data-testid="input-pelanggan"
                    />
                  ) : (
                    <input value={form.nama_pelanggan} onChange={e => setForm(f => ({ ...f, nama_pelanggan: e.target.value }))}
                      data-testid="input-pelanggan"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  )}
                  {/* Kredit retur milik pelanggan ini (owner 26 Agu 2026).
                      Tanpa ditampilkan di sini, kreditnya cuma catatan di
                      database: kasir tidak punya cara tahu pelanggan punya
                      kredit, dan pelanggan yang lupa akan kehilangan uangnya. */}
                  {kreditPelanggan > 0 && (
                    <p className="text-[11px] text-violet-700 mt-1" data-testid="nota-kredit-pelanggan">
                      Pelanggan ini punya <b>kredit retur {idr(kreditPelanggan)}</b> —
                      bisa dipakai dengan metode pembayaran &quot;Kredit Retur&quot;
                      (nama &amp; No HP cocok).
                    </p>
                  )}
                  {kreditTerkunci > 0 && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="nota-kredit-terkunci">
                      Ada kredit retur {idr(kreditTerkunci)} atas nama ini yang belum punya No HP —
                      terkunci sampai SPV melengkapinya di menu Retur › Kredit Pelanggan.
                    </p>
                  )}
                </Field>
                {/* D1/D8: No HP WAJIB tanpa pengecualian (dinormalisasi server
                    ke 628xx, minimal 10 digit) — ia kunci pemilik DP & kredit
                    retur pelanggan. */}
                <Field label="No HP *">
                  <input value={form.no_hp} inputMode="tel"
                    onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                    // Pelanggan B2B/USER yang sudah dipilih: nomornya ikut
                    // kartu. Mengubahnya di sini memutus pasangan nama+HP
                    // yang baru saja dipilih — ganti lewat pilih ulang.
                    readOnly={perluDropdownPelanggan && kartuPelangganCocok}
                    title={perluDropdownPelanggan && kartuPelangganCocok
                      ? "No HP mengikuti kartu pelanggan — pilih pelanggan lain untuk menggantinya" : ""}
                    data-testid="input-no-hp"
                    className={`w-full h-9 px-3 rounded-lg border border-slate-200 text-sm ${
                      perluDropdownPelanggan && kartuPelangganCocok ? "bg-slate-50 text-slate-600" : ""}`}
                    placeholder="08xxxx" />
                </Field>
                {(form.jenis_pelanggan || "umum") !== "umum" && (
                  <Field label="Tempo Pembayaran">
                    <select value={form.tempo_pembayaran || ""} data-testid="input-tempo"
                      onChange={e => setForm(f => ({ ...f, tempo_pembayaran: e.target.value }))}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                      <option value="">Bayar di tempat (tanpa tempo)</option>
                      <option value="net_3">Net 3</option>
                      <option value="net_7">Net 7</option>
                      <option value="net_10">Net 10</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_90">Net 90</option>
                      <option value="deposit">Deposit</option>
                    </select>
                    {form.tempo_pembayaran && (
                      <p className="text-[11px] text-amber-700 mt-1" data-testid="input-tempo-hint">
                        Nota ini jadi <b>piutang</b>. Sisa yang belum dibayar masuk ke
                        Piutang Usaha, dan Finance harus memverifikasinya dulu sebelum
                        bisa ditagih.
                      </p>
                    )}
                  </Field>
                )}
                {form.jenis_pelanggan === "user" && (
                  <Field label="PIC Persetujuan Piutang *">
                    <input value={form.pic_approval_piutang || ""} data-testid="input-pic-piutang"
                      onChange={e => setForm(f => ({ ...f, pic_approval_piutang: e.target.value }))}
                      placeholder="Siapa yang menyetujui piutang ini"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                  </Field>
                )}
                {/* Diskon NOTA & Biaya Admin DIHAPUS dari Input Nota (brief 22 Sep
                    D2/D4): potongan kini per PRODUK di tabel barang, biaya
                    admin/MDR ditanggung toko. Draf LAMA yang sudah membawanya
                    tampil BACA-SAJA dan dikirim ulang apa adanya — kalau form
                    baru diam-diam mengosongkannya, edit draf lama menghapus
                    potongan yang sudah disepakati (E13/E14). */}
                {(diskonNotaLama > 0 || adminLama) && (
                  <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                    data-testid="nota-diskon-admin-lama">
                    <p className="font-semibold text-slate-800">Draf lama — dibaca apa adanya</p>
                    {diskonNotaLama > 0 && (
                      <p>Diskon nota: <b className="font-mono">{idr(diskonNotaLama)}</b>
                        {form.diskon_pic_approval ? <> · PIC {form.diskon_pic_approval}</> : null}</p>
                    )}
                    {adminLama && (
                      <p>Biaya admin: <b className="font-mono">
                        {form.biaya_admin_mode === "persen" ? `${Number(form.biaya_admin_nilai) || 0}%` : idr(form.biaya_admin_nilai)}
                      </b></p>
                    )}
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Kolom ini sudah dihapus dari Input Nota. Nilainya ikut tersimpan tanpa
                      diubah; nota baru memakai diskon per produk.
                    </p>
                  </div>
                )}
                {/* Kolom IMEI manual tingkat nota DIHAPUS (brief owner 29 Agu,
                    Input Nota poin 1): "sudah tidak digunakan lagi, karena sudah
                    ada imei otomatis yang terkoneksi ke inventory". IMEI kini
                    HANYA dipilih per BARIS barang dari unit yang benar-benar ada
                    di gudang — mengetiknya bebas di sini membuat nota mengklaim
                    unit yang tidak pernah terdaftar.

                    Medannya sendiri tetap ada di model & dokumen lama supaya
                    nota yang sudah terlanjur mengisinya tidak berubah. */}
                <Field label={
                  <div className="flex items-center justify-between">
                    <span>Nama Sales *</span>
                    <label className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-normal cursor-pointer">
                      <input type="checkbox" checked={penjualScope === "all"} onChange={e => setPenjualScope(e.target.checked ? "all" : "mine")}
                        data-testid="penjual-scope-all" className="w-3 h-3" />
                      Pilih dari semua karyawan
                    </label>
                  </div>
                }>
                  <select value={form.nama_penjual} onChange={e => setForm(f => ({ ...f, nama_penjual: e.target.value }))}
                    data-testid="input-sales"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih karyawan —</option>
                    {availableEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Sumber Info / atribusi marketing (brief bos 11 Agu 2026) —
                  lihat components/SourceInfoField.jsx untuk alasan lengkap. */}
              <SourceInfoField
                sourceInfo={form.source_info}
                sourceRefId={form.source_ref_id}
                onChange={(v, refId) => setForm(f => ({ ...f, source_info: v, source_ref_id: refId }))}
                cfg={sourceInfoCfg}
                testidPrefix="nota-source-info"
              />

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">Barang Dibeli</p>
                  <div className="flex items-center gap-2">
                    {/* Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                        `ai_assistant`, sama dengan backend /ai-produk/parse.
                        Input manual di bawah tidak berubah sama sekali. */}
                    {bolehPakaiAI(user, can) && (
                    <button onClick={() => setAiPanel(true)} data-testid="btn-input-via-ai"
                      title="Tempel teks bebas — AI mengubahnya jadi baris barang"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-900 text-white font-medium">
                      <Sparkles className="w-3 h-3" /> Input via AI
                    </button>
                    )}
                    <button onClick={addItem} data-testid="btn-add-item"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-medium">
                      <Plus className="w-3 h-3" /> Tambah Barang
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => {
                    if (it.addition) return null;
                    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — sama
                    // pola deteksi dengan Sales.jsx (lewat KEBERADAAN resep
                    // aktif untuk SKU baris ini, bukan `it.komponen`).
                    const resepBaris = it.sku ? resepBundlingMap[it.sku.trim().toUpperCase()] : null;
                    // F3 (keputusan 23 Sep): harga baris PAKET terkunci = harga jual
                    // produk paketnya (terisi saat produk dipilih). Potongan untuk
                    // pelanggan lewat kolom Diskon, supaya "berapa diskon paket yang
                    // kita berikan" terbaca di 4190 — server menolak harga paket yang
                    // diketik ulang (`pastikan_harga_paket_terkunci`). Paket TANPA
                    // harga jual tidak dikunci (server pun tak punya angka acuan).
                    const hargaTerkunci = hargaPaketTerkunci(it, !!resepBaris);
                    const diskonBaris = Number(it.diskon) || 0;
                    return (
                    <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr,110px,100px,130px,auto] gap-2 items-start">
                      <div className="col-span-2 md:col-span-1">
                        {/* Kotak cari TUNGGAL milik EppleHub (kontrak G2): satu
                            kolom untuk nama / IMEI / kode + scan kamera, hasilnya
                            campuran unit fisik & produk. SENGAJA dipertahankan di
                            atas fitur paket ZOA — `resepBundlingMap` dioper juga
                            supaya baris paket tetap dikenali di sini. */}
                        <ItemCariField index={i} item={it}
                          bolehLookupProduk={bolehLookupProduk} bolehLookupUnit={bolehLookupUnit}
                          warehouseId={form.warehouse_id} onWarehouseHint={onWarehouseHint}
                          resepBundlingMap={resepBundlingMap}
                          deriveKode={deriveKode}
                          /* Nama prop tetap `deriveKategori` (kontrak ItemCariField),
                              tapi fungsinya kini `deriveKategoriTeksBebas` dari lib/kategoriBarang
                              — sejak migrasi ZOA tahap 1 identifier lama tidak ada lagi, dan
                              merujuknya melempar ReferenceError saat baris item dirender. */
                          deriveKategori={deriveKategoriTeksBebas}
                          onPatch={(patch) => patchItem(i, patch)} />
                        {/* IMEI baris ini HANYA muncul untuk barang yang sudah tertaut SKU
                            (brief bos poin 4) — barang ketik bebas tanpa SKU tidak pernah
                            dipaksa mengisi IMEI, lihat docstring ItemImeiField. Baris PAKET
                            (poin 8) tidak pernah punya unit fisik untuk SKU paketnya sendiri
                            — IMEI-nya per KOMPONEN lewat BundleKomponenPicker di bawah. */}
                        {bolehLookupUnit && it.sku && !resepBaris && (
                          <ItemImeiField index={i} item={it} bolehLookup={bolehLookupUnit}
                            warehouseId={form.warehouse_id} onWarehouseHint={onWarehouseHint}
                            imeiTerpakaiLain={semuaImeiDipakai({ row: i })}
                            onPatch={(patch) => patchItem(i, patch)} />
                        )}
                        {/* Pemilih komponen paket versi ZOA — menggantikan daftar
                            komponen inline B4 (kontraknya lebih lengkap: resep,
                            cache info produk, dan pemilihan IMEI jadi satu). */}
                        {(resepBaris || (Array.isArray(it.komponen) && it.komponen.length > 0)) && (
                          <BundleKomponenPicker index={i} resep={resepBaris} komponen={it.komponen}
                            onChange={(next) => patchItem(i, { komponen: next })}
                            bolehLookupUnit={bolehLookupUnit}
                            imeiTerpakaiLain={kumpulkanSemuaImeiForm(form.items)}
                            produkInfoCache={produkInfoCache} ensureProdukInfo={ensureProdukInfo} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <InputRupiah value={it.harga} onChange={v => updItem(i, "harga", v)}
                          placeholder="Harga" testid={`item-price-${i}`} disabled={hargaTerkunci}
                          className={`h-9 w-full px-2 rounded border border-slate-200 text-xs text-right font-mono ${
                            hargaTerkunci ? "bg-slate-50 text-slate-600" : ""}`} />
                        {hargaTerkunci && (
                          <p className="text-[10px] text-slate-500 inline-flex items-center gap-1" data-testid={`item-harga-terkunci-${i}`}>
                            <Lock className="w-3 h-3" /> Harga paket terkunci
                          </p>
                        )}
                        {/* Backend menolak harga Rp0 kecuali ditandai bonus — checkbox ini
                            BUKAN cara "mengakali" harga, hanya untuk barang yang memang
                            diberikan gratis (bonus/hadiah/garansi pembelian). */}
                        <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer"
                          title="Centang kalau barang ini memang diberikan GRATIS (bonus/hadiah/garansi) — bukan cara mengosongkan harga jual.">
                          <input type="checkbox" checked={!!it.is_bonus}
                            onChange={e => updItem(i, "is_bonus", e.target.checked)}
                            data-testid={`item-bonus-${i}`} className="w-3 h-3 accent-emerald-600" />
                          Bonus/Gratis
                        </label>
                      </div>
                      {/* Diskon PRODUK per baris (brief 22 Sep D3/D5) — nominal Rp,
                          maksimal harga baris. `harga` tetap BRUTO: pendapatan
                          dijurnal bruto dan diskonnya ke 4190 (satu PIC per nota).
                          Terkunci untuk barang bonus (harganya memang 0) dan untuk
                          draf lama yang masih membawa diskon nota (campuran ditolak). */}
                      <div className="space-y-1">
                        <InputRupiah value={it.diskon} onChange={v => updItem(i, "diskon", v)}
                          placeholder="Diskon" testid={`item-diskon-${i}`}
                          disabled={!!it.is_bonus || diskonNotaLama > 0}
                          className={`h-9 w-full px-2 rounded border text-xs text-right font-mono ${
                            diskonBaris > (Number(it.harga) || 0) ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} />
                        {diskonBaris > 0 && (
                          <p className={`text-[10px] text-right ${diskonBaris > (Number(it.harga) || 0) ? "text-rose-700" : "text-slate-500"}`}
                            data-testid={`item-bersih-${i}`}>
                            {diskonBaris > (Number(it.harga) || 0) ? "melebihi harga" : `bersih ${idr((Number(it.harga) || 0) - diskonBaris)}`}
                          </p>
                        )}
                      </div>
                      {/* Baris bertaut SKU: kategori diambil APA ADANYA dari master
                          produk (lihat ItemCariField.pilih()) — dikunci, bukan
                          dropdown, supaya sales tidak bisa diam-diam menimpanya
                          dengan kategori yang salah (akun neraca ikut salah kalau
                          itu terjadi, lihat memory/MIGRASI-KATEGORI-23AGU.md).
                          Lepas SKU dulu (tombol × di badge SKU) kalau memang perlu
                          diketik bebas. Baris TANPA SKU tetap dropdown biasa, karena
                          tidak ada sumber kebenaran lain selain pilihan sales. */}
                      {it.sku ? (
                        <div data-testid={`item-kategori-${i}`}
                          title="Kategori mengikuti master produk — lepas tautan SKU untuk mengubah manual"
                          className="h-9 px-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center">
                          {labelKategori(it.kategori)}
                        </div>
                      ) : (
                        <select value={normalKategori(it.kategori) || KATEGORI_DEFAULT} onChange={e => updItem(i, "kategori", e.target.value)}
                          data-testid={`item-kategori-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                      )}
                      <button onClick={() => rmItem(i)} disabled={form.items.length <= 1}
                        className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    );
                  })}
                </div>
                {can("inventory_produk", "view") && <SalesExtrasPicker form={form} setForm={setForm}/>}
                <p className="text-right text-sm font-semibold text-emerald-700 mt-2">
                  Total Barang: <span className="font-mono text-base">{idr(totalHarga)}</span>
                </p>
                {diskonBarisForm > 0 && (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                    {/* SATU PIC per nota untuk seluruh diskon produk (D5) — diskon
                        adalah uang yang sengaja tidak ditagih; tanpa nama yang
                        menyetujuinya, potongan besar tidak bisa ditanyakan ke siapa pun. */}
                    <Field label="PIC Approval Diskon *">
                      <input value={form.diskon_pic_approval || ""} data-testid="input-diskon-pic"
                        onChange={e => setForm(f => ({ ...f, diskon_pic_approval: e.target.value }))}
                        placeholder="Siapa yang menyetujui diskon produk ini"
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                    </Field>
                    <p className="text-right text-xs text-rose-700" data-testid="nota-diskon-produk-total">
                      Diskon produk: <span className="font-mono font-semibold">−{idr(diskonBarisForm)}</span>
                      <span className="block text-[10px] text-slate-500">dicatat di akun 4190, bukan menurunkan harga</span>
                    </p>
                  </div>
                )}
                {/* PPN (keputusan owner 18 Agu 2026): CV ZOA berstatus PKP,
                    tarif 11%, harga barang di atas BELUM termasuk PPN — dicentang
                    di sini untuk menambahkan PPN sebagai kolom terpisah. */}
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!form.kena_ppn}
                      onChange={e => setForm(f => ({ ...f, kena_ppn: e.target.checked }))}
                      data-testid="input-kena-ppn" className="w-3.5 h-3.5 accent-emerald-600" />
                    Kena PPN 11% (harga di atas belum termasuk PPN)
                  </label>
                  {/* DPP & PPN hitungan SERVER (D7: DPP = barang − diskon produk,
                      menunggu konfirmasi konsultan pajak owner). */}
                  {form.kena_ppn && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs space-y-0.5"
                      data-testid="ringkasan-ppn">
                      <div className="flex justify-between text-slate-600">
                        <span>DPP (sesudah diskon produk)</span>
                        <span className="font-mono">{rincian ? idr(rincian.dpp) : "…"}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>PPN ({rincian ? rincian.ppn_persen : 11}%)</span>
                        <span className="font-mono">{rincian ? idr(rincian.ppn_nominal) : "…"}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ================= PEMBAYARAN (brief 22 Sep D4-D7) =================
                  Urutan mengikuti brief: metode pembayaran UTAMA + nominal, centang
                  "tambah pembayaran" (keterangan bebas | metode | nominal, boleh lebih
                  dari satu), lalu Trade-in dan Downpayment DI BAWAHNYA sebagai
                  PENGURANG yang harus dibayar, lalu ringkasan tagihan hitungan
                  server. Kolom "Harga Terima" dan toggle Split dihapus: uang nota =
                  jumlah baris ini (+ trade-in + DP), ditegakkan server. */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-3" data-testid="nota-pembayaran">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran
                  </p>
                  {rincian && (
                    <p className="text-[11px] text-slate-500">
                      Harus dibayar: <b className="font-mono text-slate-900">{idr(rincian.harus_dibayar)}</b>
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr,200px] gap-2">
                  <PaymentMethodSelect
                    value={bayarUtama.method}
                    onChange={v => updPay(0, "method", v)}
                    groups={paymentGroupsNota}
                    testid="nota-metode"
                  />
                  <div className="flex gap-1">
                    <InputRupiah placeholder="Nominal" value={bayarUtama.amount || ""}
                      onChange={v => updPay(0, "amount", v)}
                      testid="nota-pay-0-amount"
                      className="flex-1 min-w-0 h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono bg-white" />
                    <button type="button" onClick={() => isiSisaPembayaran(0)} data-testid="nota-pay-0-isi-sisa"
                      title="Isi dengan yang masih harus dibayar (hitungan server) dikurangi baris lain"
                      className="h-9 px-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-medium whitespace-nowrap">
                      Isi sisa
                    </button>
                  </div>
                  <input value={bayarUtama.keterangan || ""} maxLength={120}
                    onChange={e => updPay(0, "keterangan", e.target.value)}
                    placeholder="Keterangan / No. Kontrak leasing (opsional)"
                    data-testid="nota-pay-0-keterangan"
                    className="sm:col-span-2 h-8 px-2 rounded border border-slate-200 text-xs bg-white" />
                </div>

                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={(form.payments || []).length > 1}
                    onChange={e => setTambahPembayaran(e.target.checked)}
                    data-testid="nota-tambah-pembayaran" className="w-3.5 h-3.5 accent-emerald-600" />
                  Tambah pembayaran (metode lain / porsi leasing)
                </label>
                {(form.payments || []).length > 1 && (
                  <div className="space-y-2" data-testid="nota-pembayaran-tambahan">
                    {(form.payments || []).slice(1).map((p, j) => {
                      const i = j + 1;
                      return (
                        <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,170px,auto] gap-2 items-center">
                          {/* Kolom bebas (D3): keterangan / No. Kontrak — tercetak
                              di faktur, TIDAK mengubah jurnal (akunnya dari metode). */}
                          <input value={p.keterangan || ""} maxLength={120}
                            onChange={e => updPay(i, "keterangan", e.target.value)}
                            placeholder="Keterangan / No. Kontrak"
                            data-testid={`nota-pay-${i}-keterangan`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <PaymentMethodSelect
                            value={p.method}
                            onChange={v => updPay(i, "method", v)}
                            groups={paymentGroupsNota}
                            testid={`nota-pay-${i}-method`}
                          />
                          <div className="flex gap-1">
                            <InputRupiah placeholder="Nominal" value={p.amount || ""}
                              onChange={v => updPay(i, "amount", v)}
                              testid={`nota-pay-${i}-amount`}
                              className="flex-1 min-w-0 h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono bg-white" />
                            <button type="button" onClick={() => isiSisaPembayaran(i)} data-testid={`nota-pay-${i}-isi-sisa`}
                              title="Isi dengan sisa yang masih harus dibayar"
                              className="h-9 px-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-medium">
                              Sisa
                            </button>
                          </div>
                          <button type="button" onClick={() => rmPay(i)} data-testid={`nota-pay-${i}-hapus`}
                            title="Hapus baris pembayaran ini"
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 justify-self-end">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                    <button type="button" onClick={addPay} data-testid="btn-add-pay"
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                      <Plus className="w-3.5 h-3.5" /> Tambah baris pembayaran
                    </button>
                  </div>
                )}

                {/* TRADE-IN (D5) — di bawah metode pembayaran: nilai unitnya
                    MENGURANGI yang harus dibayar (dijurnal ke kas penampung
                    trade-in cabang, D8), bukan baris pembayaran. */}
                <div className="pt-2 border-t border-slate-200">
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!form.trade_in}
                      onChange={e => setForm(f => ({ ...f, trade_in: e.target.checked }))}
                      data-testid="input-tradein" className="w-3.5 h-3.5 accent-violet-600" />
                    <Repeat className="w-3.5 h-3.5" /> Ada unit trade-in (mengurangi yang harus dibayar)
                  </label>
                </div>
                {form.trade_in && (
                  <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/40">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 inline-flex items-center gap-1.5">
                        <Repeat className="w-3.5 h-3.5" /> Unit Trade-in
                      </p>
                      <button onClick={addTradeItem} type="button" data-testid="btn-add-tradein"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 font-medium">
                        <Plus className="w-3 h-3" /> Tambah Unit
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(form.trade_in_items || []).map((t, i) => (
                        <div key={i} className="rounded-lg border border-violet-100 bg-violet-50/40 p-2 space-y-2">
                         <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          <input value={t.nama}
                            onChange={e => updTradeItem(i, "nama", e.target.value)}
                            placeholder="Nama barang *"
                            data-testid={`tradein-name-${i}`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <select value={t.status || ""} data-testid={`tradein-status-${i}`}
                            onChange={e => updTradeItem(i, "status", e.target.value)}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            <option value="">Status *</option>
                            {TI_STATUS.map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <input value={t.warna || ""} data-testid={`tradein-warna-${i}`}
                            onChange={e => updTradeItem(i, "warna", e.target.value)}
                            placeholder="Warna *"
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <input value={t.bh || ""} data-testid={`tradein-bh-${i}`}
                            onChange={e => updTradeItem(i, "bh", e.target.value)}
                            placeholder="BH (mis. 85%)"
                            title="Battery Health — opsional"
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <select value={t.kelengkapan || ""} data-testid={`tradein-kelengkapan-${i}`}
                            onChange={e => updTradeItem(i, "kelengkapan", e.target.value)}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            <option value="">Kelengkapan *</option>
                            {TI_KELENGKAPAN.map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                         </div>
                         {/* Output tergabung diperlihatkan SEBELUM disimpan (poin 5)
                             — kasir bisa melihat nama yang akan benar-benar tercatat,
                             bukan menebaknya dari lima kolom terpisah. */}
                         {rakitNamaTradeIn(t) && (
                           <p className="text-[11px] font-mono text-violet-800" data-testid={`tradein-preview-${i}`}>
                             → {rakitNamaTradeIn(t)}
                           </p>
                         )}
                         {/* Di HP catatan satu baris penuh, IMEI & nilai berdampingan —
                             lebar tetap 170/140px dulu mendorong modal melebar
                             melewati layar 400px (form terpotong di kanan). */}
                         <div className="grid grid-cols-[minmax(0,1fr),minmax(0,1fr),auto] sm:grid-cols-[1fr,170px,140px,auto] gap-2 items-center">
                          <input value={t.catatan || ""} data-testid={`tradein-catatan-${i}`}
                            onChange={e => updTradeItem(i, "catatan", e.target.value)}
                            placeholder="Catatan (opsional)"
                            className="col-span-3 sm:col-span-1 min-w-0 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                          <input value={t.imei || ""}
                            onChange={e => updTradeItem(i, "imei", e.target.value)}
                            placeholder="IMEI / SN *"
                            inputMode="numeric"
                            maxLength={30}
                            title="IMEI/SN unit trade-in — bentuk bebas. Boleh dikosongkan."
                            data-testid={`tradein-imei-${i}`}
                            className="min-w-0 w-full h-9 px-2 rounded border border-slate-200 text-xs font-mono bg-white" />
                          <InputRupiah value={t.harga || ""}
                            onChange={v => updTradeItem(i, "harga", v)}
                            placeholder="Nilai Rp"
                            testid={`tradein-price-${i}`}
                            className="min-w-0 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
                          <button onClick={() => rmTradeItem(i)} type="button"
                            disabled={(form.trade_in_items || []).length <= 1}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                         </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-right text-xs text-violet-700 mt-2">
                      Total Trade-in: <span className="font-mono font-bold text-base" data-testid="nota-total-tradein">
                        {rincian ? idr(rincian.trade_in) : "…"}
                      </span>
                    </p>
                  </div>
                )}

                {/* DOWNPAYMENT (D7) — di bawah metode pembayaran: mengurangi yang
                    harus dibayar dan ikut tercetak. Kolom TIDAK disembunyikan saat
                    daftarnya kosong: versi lama menyembunyikannya, sehingga cacat
                    pencarian nomor HP (`re.escape` pada pencocokan persis) tampak
                    sebagai "fitur DP tidak ada" — gagal tanpa satu pun pesan,
                    selama dua minggu tanpa ada yang tahu. Pemilik DP diperiksa
                    server lewat No HP (D8). */}
                {dpDicari && (
                  <div className="pt-2 border-t border-slate-200">
                    <Field label="Downpayment (DP) — mengurangi yang harus dibayar">
                      {dpCocok.length === 0 ? (
                        <p className="text-xs text-slate-500 py-2" data-testid="input-dp-kosong">
                          Tidak ada DP tersisa untuk No HP pelanggan ini.
                        </p>
                      ) : (
                        <select value={form.dp_id || ""} data-testid="input-dp"
                          onChange={e => setForm(f => ({ ...f, dp_id: e.target.value, dp_nominal: "" }))}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                          <option value="">— tanpa DP —</option>
                          {dpCocok.map(d => (
                            <option key={d.id} value={d.id}>
                              {d.pelanggan_nama} · {d.code} · {d.tanggal} · sisa {idr(d.sisa)}
                            </option>
                          ))}
                        </select>
                      )}
                      {dpLainHp > 0 && (
                        <p className="text-[11px] text-amber-700 mt-1" data-testid="input-dp-lain-hp">
                          Ada {dpLainHp} DP atas nama mirip dengan No HP lain — DP hanya bisa dipakai
                          nota dengan No HP yang sama dengan No HP DP-nya.
                        </p>
                      )}
                      {dpTerpilih && (
                        <>
                          <InputRupiah value={form.dp_nominal} bolehKosong
                            onChange={v => setForm(f => ({ ...f, dp_nominal: v === null ? "" : v }))}
                            testid="input-dp-nominal"
                            placeholder="kosongkan = pakai seluruh sisa (sampai lunas)"
                            className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm text-right font-mono" />
                          <div className="mt-1 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-2 text-[11px] text-emerald-900 space-y-0.5"
                               data-testid="input-dp-hint">
                            <div className="flex justify-between gap-3">
                              <span>Saldo DP</span>
                              <b data-testid="input-dp-saldo">{idr(dpSaldo)}</b>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span>Nominal digunakan</span>
                              <b data-testid="input-dp-dipakai">{rincian ? idr(rincian.dp) : "…"}</b>
                            </div>
                            <div className="flex justify-between gap-3 border-t border-emerald-200 pt-0.5">
                              <span>Sisa DP pelanggan</span>
                              <b data-testid="input-dp-sisa">{rincian ? idr(Math.max(0, dpSaldo - rincian.dp)) : "…"}</b>
                            </div>
                            <div className="flex justify-between gap-3 border-t border-emerald-200 pt-0.5">
                              <span>Sisa yang harus dibayar</span>
                              <b data-testid="input-dp-sisa-bayar">{rincian ? idr(rincian.harus_dibayar) : "…"}</b>
                            </div>
                          </div>
                        </>
                      )}
                    </Field>
                  </div>
                )}

                {/* RINGKASAN — angka hitungan SERVER untuk isi form yang sekarang.
                    Selisih MEMBLOKIR simpan (menggantikan confirm() lama yang
                    membiarkan selisih lolos lalu ditambal diam-diam ke 4900/6900). */}
                <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-1" data-testid="nota-ringkasan-tagihan">
                  {!rincian ? (
                    <p className={pratinjau.gagal && pratinjau.kunci === kunciPratinjau ? "text-rose-700" : "text-slate-500"}
                      data-testid="nota-ringkasan-memuat">
                      {pratinjau.gagal && pratinjau.kunci === kunciPratinjau
                        ? pratinjau.gagal
                        : (pesanPratinjau[0] || "Menghitung tagihan…")}
                    </p>
                  ) : (
                    <>
                      <BarisRingkas label="Total barang" nilai={rincian.total_barang} />
                      {rincian.diskon > 0 && (
                        <BarisRingkas label={diskonNotaLama > 0 ? "− Diskon nota (draf lama)" : "− Diskon produk"}
                          nilai={-rincian.diskon} kelas="text-rose-700" />
                      )}
                      {rincian.biaya_admin_rp > 0 && (
                        <BarisRingkas label="+ Biaya admin (draf lama)" nilai={rincian.biaya_admin_rp} />
                      )}
                      {rincian.ppn_nominal > 0 && (
                        <BarisRingkas label={`+ PPN ${rincian.ppn_persen}%`} nilai={rincian.ppn_nominal} />
                      )}
                      <BarisRingkas label="Nilai nota" nilai={rincian.tagihan} tebal testid="nota-nilai-nota" />
                      {rincian.trade_in > 0 && (
                        <BarisRingkas label="− Trade-in" nilai={-rincian.trade_in} kelas="text-violet-700" />
                      )}
                      {rincian.dp > 0 && (
                        <BarisRingkas label="− Downpayment (DP)" nilai={-rincian.dp} kelas="text-emerald-700" />
                      )}
                      <BarisRingkas label="Harus dibayar" nilai={rincian.harus_dibayar} tebal testid="nota-harus-dibayar" />
                      <BarisRingkas label="Jumlah baris pembayaran" nilai={jumlahBaris} testid="nota-jumlah-baris" />
                      {tagihanCocok ? (
                        <p className="pt-1 border-t border-slate-100 font-semibold text-emerald-700 flex items-center gap-1.5"
                          data-testid="nota-status-bayar">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {notaBertempo && (pratinjau.data.sisa_piutang || 0) > 0
                            ? `Sisa ${idr(pratinjau.data.sisa_piutang)} jadi piutang (tempo)`
                            : "Pembayaran sesuai"}
                        </p>
                      ) : (
                        <div className="pt-1 border-t border-slate-100 text-rose-700 space-y-0.5" data-testid="nota-status-bayar">
                          {(pesanPratinjau.length ? pesanPratinjau : ["Jumlah pembayaran belum sesuai."]).map((m, k) => (
                            <p key={k} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {m}</p>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* "Harga Terima" diganti "Total Pembayaran" (brief 22 Sep D6/D1):
                  HANYA bisa diisi KASIR (`summary.is_kasir` — gerbang yang SAMA
                  dengan `_is_kasir` server; kiriman orang lain dibuang server).
                  Letaknya di atas Catatan. Terisi dan tidak sama = nota tidak bisa
                  diproses. Kosong = kasir mengisinya di dialog Setujui. Disimpan di
                  medan sendiri, BUKAN `harga_terima` (medan itu menanggung tiga arti). */}
              <Field label="Total Pembayaran (diisi kasir)">
                {canApprove ? (
                  <>
                    <InputRupiah bolehKosong value={form.total_pembayaran_kasir}
                      onChange={v => setForm(f => ({ ...f, total_pembayaran_kasir: v === null ? "" : v }))}
                      testid="input-total-pembayaran"
                      placeholder="ketik uang yang benar-benar diterima"
                      className={`w-full h-9 px-3 rounded-lg border text-sm text-right font-mono ${
                        totalKasirBeda ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} />
                    {totalKasirBeda ? (
                      <p className="text-[11px] text-rose-700 mt-1 flex items-start gap-1" data-testid="alert-total-pembayaran">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        Total Pembayaran {idr(form.total_pembayaran_kasir)} tidak sama dengan jumlah
                        pembayaran di nota {idr(jumlahBaris)} — nota tidak bisa diproses.
                      </p>
                    ) : !totalKasirTerisi ? (
                      <p className="text-[11px] text-slate-500 mt-1">
                        Boleh dikosongkan di sini — wajib diisi saat menyetujui nota.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <div data-testid="input-total-pembayaran-terkunci"
                    className="h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Diisi kasir saat menyetujui nota
                  </div>
                )}
              </Field>

              <Field label="Catatan">
                <textarea value={form.catatan} onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </Field>
            </div>

            {/* Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — alasan tombol
                Kirim/Simpan tertahan, aturan #3 CLAUDE.md. */}
            {bundleAlasanTertahan && (
              <p className="mx-4 mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                data-testid="nota-bundle-alasan-tertahan">
                {bundleAlasanTertahan}
              </p>
            )}
            {/* Alasan tombol simpan tertahan karena UANG (D1/D2/D10) — selisih
                pembayaran kini MEMBLOKIR, jadi alasannya harus terbaca di sini,
                bukan cuma tombol yang mati tanpa penjelasan. */}
            {!bundleAlasanTertahan && alasanUangTertahan && !menghitungTagihan && (
              <p className="mx-4 mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                data-testid="nota-simpan-tertahan">
                {alasanUangTertahan}
              </p>
            )}
            {scanInline && modalMode === "create" && (
              <div className="mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2"
                data-testid="nota-antrian-scan-inline">
                {pesanTolakAntrian && (
                  <p className="text-xs text-rose-700">{pesanTolakAntrian}</p>
                )}
                <p className="text-xs text-amber-900">
                  Pindai tiket antrian pelanggan ini, lalu tekan <b>Kirim ke Kasir</b> lagi. Isian nota tidak hilang.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <BarcodeScanner
                    onDetected={(kode) => panggilTiketInline(kode)}
                    testId="nota-antrian-scan-btn"
                    title="Scan barcode tiket antrian"
                  />
                  <input
                    value={kodeInline} onChange={e => setKodeInline(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); panggilTiketInline(kodeInline); } }}
                    placeholder="atau ketik kode tiket"
                    data-testid="nota-antrian-scan-input"
                    className="h-9 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[200px] bg-white"
                  />
                  <button type="button" onClick={() => panggilTiketInline(kodeInline)} disabled={busyPanggil}
                    data-testid="nota-antrian-scan-panggil"
                    className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50">
                    {busyPanggil ? "Memanggil..." : "Panggil"}
                  </button>
                </div>
              </div>
            )}
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={notaGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submit} disabled={saving || !!bundleAlasanTertahan || !!alasanUangTertahan} data-testid="btn-save-nota"
                title={bundleAlasanTertahan || alasanUangTertahan || undefined}
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                <Send className="w-4 h-4" /> {saving ? "Menyimpan..." : (modalMode === "create" ? "Kirim ke Kasir" : "Simpan Perubahan")}
              </button>
            </div>
            {notaGuard.confirming && (
              <UnsavedGuardBar onStay={notaGuard.cancelClose} onDiscard={notaGuard.confirmDiscard} testid="nota-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Dialog konfirmasi PPN sebelum simpan (brief bos) — z-[60] supaya
          tampil DI ATAS modal input (z-50) yang masih terbuka di belakangnya. */}
      {ppnConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={() => setPpnConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="ppn-confirm-modal">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Konfirmasi PPN 11%</h3>
              <button onClick={() => setPpnConfirm(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Nota ini akan dicatat KENA PPN. PPN akan dipisah dari pendapatan dan
                dicatat sebagai kewajiban pajak (PPN Keluaran) yang wajib disetor —
                pastikan sudah benar sebelum melanjutkan.
              </p>
              {/* Angka hitungan SERVER (pratinjau) — sama dengan yang dijurnal. */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-0.5">
                <div className="flex justify-between text-slate-600">
                  <span>DPP (sesudah diskon produk)</span>
                  <span className="font-mono">{rincian ? idr(rincian.dpp) : "…"}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>PPN ({rincian ? rincian.ppn_persen : 11}%)</span>
                  <span className="font-mono">{rincian ? idr(rincian.ppn_nominal) : "…"}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200">
                  <span>Nilai nota</span>
                  <span className="font-mono">{rincian ? idr(rincian.tagihan) : "…"}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPpnConfirm(null)} data-testid="btn-ppn-kembali"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Kembali
              </button>
              <button onClick={() => doSave(ppnConfirm)} disabled={saving} data-testid="btn-ppn-lanjutkan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {saving ? "Menyimpan..." : "Lanjutkan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi tiket untuk pengguna wajib-scan — ditanya, bukan dipilihkan
          diam-diam: FL bisa memegang tiket pelanggan A sementara nota yang
          sedang dibuat untuk pelanggan B. */}
      {konfirmasiTiket && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setKonfirmasiTiket(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="nota-antrian-konfirmasi">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Ticket className="w-4 h-4" /> Tiket antrian</h3>
              <button onClick={() => setKonfirmasiTiket(null)} data-testid="nota-antrian-konfirmasi-tutup"
                className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              {konfirmasiTiket.pilihan.length === 1 ? (
                <p className="text-sm text-slate-800">
                  Nota ini untuk tiket <b>No. {konfirmasiTiket.pilihan[0].nomor_antrian}</b>?
                  <span className="block text-xs text-slate-500 mt-0.5 break-all">
                    {konfirmasiTiket.pilihan[0].kode_antrian} · {konfirmasiTiket.pilihan[0]._keterangan}
                  </span>
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-800">Nota ini untuk tiket yang mana?</p>
                  <ul className="space-y-1.5">
                    {konfirmasiTiket.pilihan.map(t => (
                      <li key={t.kode_antrian}>
                        <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer">
                          <input type="radio" name="nota-antrian-pilih"
                            checked={konfirmasiTiket.kode === t.kode_antrian}
                            onChange={() => setKonfirmasiTiket(k => ({ ...k, kode: t.kode_antrian }))}
                            data-testid={`nota-antrian-pilih-${t.kode_antrian}`}
                            className="mt-1" />
                          <span className="min-w-0">
                            <b>No. {t.nomor_antrian}</b>
                            <span className="block text-xs text-slate-500 break-all">{t.kode_antrian} · {t._keterangan}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 p-4 flex flex-wrap justify-end gap-2">
              <button onClick={() => { setKonfirmasiTiket(null); navigate("/antrian?tab=layani"); }}
                data-testid="nota-antrian-konfirmasi-bukan"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Bukan / scan tiket baru
              </button>
              <button onClick={pilihTiketKonfirmasi} disabled={!konfirmasiTiket.kode}
                data-testid="nota-antrian-konfirmasi-ya"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                Ya
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel chat AI — drawer fixed di atas modal (z-60 > z-50), unmount saat
          modal ditutup sehingga riwayat chat otomatis bersih untuk nota berikutnya */}
      {modal && aiPanel && bolehPakaiAI(user, can) && (
        <AIProductChat mode="sales" onApply={applyAiItems} onClose={() => setAiPanel(false)} />
      )}

      {/* Detail modal */}
      {detail && (
        <NotaDetail
          n={detail}
          user={user}
          canApprove={canApprove}
          onClose={() => setDetail(null)}
          onEdit={() => openEdit(detail)}
          onApprove={() => bukaSetujui(detail)}
          onReject={() => openReject(detail)}
          onDelete={() => deleteNota(detail)}
        />
      )}

      {/* Dialog Setujui (D1) — z-[60], DI ATAS modal detail yang bisa masih
          terbuka di belakangnya. Satu dialog untuk ketiga tombol Setujui. */}
      {setujuiNota && (
        <DialogSetujuiNota
          n={setujuiNota}
          onClose={() => setSetujuiNota(null)}
          onSelesai={setelahDisetujui}
          onGagal={load}
        />
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="reject-modal">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Tolak Nota</h3>
              <button onClick={() => setRejectModal(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-600 mb-3">
                Menolak nota dari <b>{rejectModal.nama_penjual}</b> untuk pelanggan <b>{rejectModal.nama_pelanggan}</b>.
              </p>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                autoFocus rows={3} placeholder="Alasan penolakan..."
                data-testid="input-reject-reason"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setRejectModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={doReject} data-testid="btn-confirm-reject"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium">
                Tolak Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment methods manager modal */}
      {methodsModal && (
        <MetodePembayaranManager
          groups={paymentGroupsAll}
          onClose={() => setMethodsModal(false)}
          onChange={loadMethods}
        />
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== NotaDetail (rich preview) ====================
// Brief 22 Sep: diskon per baris, keterangan pembayaran, trade-in, dan DP
// tampil di bawah pembayaran; angka nilai nota / harus dibayar dari server
// (`_tampilan_draf`), bukan dihitung ulang di sini.
function NotaDetail({ n, user, canApprove, onClose, onEdit, onApprove, onReject, onDelete }) {
  const meta = STATUS_META[n.status] || STATUS_META.pending;
  const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
  const rinci = !!n.pembayaran_rinci;
  const barisBayar = (n.payments || []).filter(p => (Number(p.amount) || 0) > 0 || !rinci);
  const totalTradeIn = (n.trade_in_items || []).reduce((s, t) => s + (parseFloat(t.harga) || 0), 0);
  const adaDiskonBaris = (n.items || []).some(it => (Number(it.diskon) || 0) > 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="nota-detail-modal">
        <div className={`bg-gradient-to-r from-${meta.color}-600 to-${meta.color}-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl`}>
          <div>
            <p className="text-[11px] uppercase tracking-wider opacity-90">Detail Nota Sales</p>
            <h3 className="font-display text-lg font-semibold">{n.nama_pelanggan}</h3>
            <p className="text-[11px] opacity-90 mt-0.5 inline-flex items-center gap-1">
              <meta.icon className="w-3 h-3" /> {meta.label}
              {n.status === "approved" && n.approved_ref_id && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-mono">→ Sales #{n.approved_ref_id.slice(0, 8)}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Customer + sales grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <InfoRow icon={User} label="Nama Pelanggan" value={n.nama_pelanggan} />
            <InfoRow icon={Phone} label="No HP" value={n.no_hp || "-"} />
            <InfoRow icon={FileText} label="IMEI" value={n.imei || "-"} />
            <InfoRow icon={ShoppingCart} label="Sales" value={n.nama_penjual} />
            <InfoRow icon={MapPin} label="Cabang" value={n.branch || "-"} />
            <InfoRow icon={Clock} label="Tanggal · Waktu" value={`${n.tanggal} · ${n.waktu}`} />
          </div>

          {/* Items — diskon PRODUK per baris (D5) tampil di barisnya sendiri */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" /> Barang ({n.n_items})
            </p>
            <div className="space-y-1">
              {(n.items || []).map((it, i) => {
                const d = Number(it.diskon) || 0;
                return (
                  <div key={i} className="flex justify-between gap-2 text-xs bg-slate-50 rounded px-2 py-1.5"
                    data-testid={`nota-detail-item-${i}`}>
                    <span>
                      {it.nama_barang}
                      <span className="ml-1 px-1 py-0 rounded bg-slate-200 text-slate-600 text-[9px] font-bold">{it.kode || "HP"}</span>
                      {it.is_bonus && (
                        <span className="ml-1 px-1 py-0 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">Bonus</span>
                      )}
                    </span>
                    <span className="text-right shrink-0">
                      <span className={`font-mono font-semibold ${d > 0 ? "line-through text-slate-400 font-normal" : ""}`}>{idr(it.harga)}</span>
                      {d > 0 && (
                        <span className="block text-[10px]" data-testid={`nota-detail-item-diskon-${i}`}>
                          <span className="text-rose-700">diskon −{idr(d)}</span>
                          {" · "}<span className="font-mono font-semibold text-slate-900">{idr((Number(it.harga) || 0) - d)}</span>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Ringkasan tagihan dari server (B3) */}
            <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs space-y-0.5" data-testid="nota-detail-ringkasan">
              <BarisRingkas label="Total barang" nilai={n.total_harga} />
              {(n.diskon_tampil || 0) > 0 && (
                <BarisRingkas label={adaDiskonBaris ? "− Diskon produk" : "− Diskon nota"} nilai={-n.diskon_tampil}
                  kelas="text-rose-700" />
              )}
              {adaDiskonBaris && n.diskon_pic_approval && (
                <p className="text-[10px] text-slate-500 text-right">PIC diskon: {n.diskon_pic_approval}</p>
              )}
              {n.kena_ppn && (
                <p className="text-[10px] text-emerald-700 text-right" data-testid="nota-detail-ppn">
                  Kena PPN — nominal pasti dihitung saat approve
                </p>
              )}
              {n.nilai_nota != null && (
                <BarisRingkas label="Nilai nota" nilai={n.nilai_nota} tebal testid="nota-detail-nilai-nota" />
              )}
            </div>
          </div>

          {/* Pembayaran — baris (metode + keterangan), lalu trade-in & DP
              sebagai PENGURANG di bawahnya (urutan brief D5/D7). */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <CreditCard className="w-3 h-3" /> Pembayaran
            </p>
            <div className="space-y-1">
              {barisBayar.length > 0 ? barisBayar.map((p, i) => (
                <div key={i} className="flex justify-between gap-2 text-xs bg-sky-50 border border-sky-200 rounded px-2 py-1.5"
                  data-testid={`nota-detail-bayar-${i}`}>
                  <span className="text-sky-900 font-medium">
                    {p.method}
                    {p.keterangan && <span className="block text-[10px] font-normal text-sky-700">{p.keterangan}</span>}
                  </span>
                  <span className="font-mono font-semibold text-sky-900">{idr(p.amount)}</span>
                </div>
              )) : (
                <p className="text-xs bg-slate-50 rounded px-2 py-1.5">
                  <b>{n.metode_pembayaran || "Cash"}</b>
                  <span className="text-slate-500 ml-2">{rinci ? "(tanpa baris pembayaran)" : "(satu metode — nota format lama)"}</span>
                </p>
              )}
              {n.trade_in && (
                n.trade_in_items && n.trade_in_items.length > 0 ? (
                  n.trade_in_items.map((t, i) => (
                    <div key={`ti-${i}`} className="flex justify-between items-start gap-2 text-xs bg-violet-50 border border-violet-200 rounded px-2 py-1.5">
                      <span className="text-violet-900 font-medium">
                        <Repeat className="w-3 h-3 inline mr-1" />Trade-in: {t.nama || <span className="italic text-slate-400">tanpa nama</span>}
                        {t.imei && (
                          <span className="block font-mono text-[10px] text-violet-600 mt-0.5"
                            data-testid={`nota-detail-tradein-imei-${i}`}>IMEI {t.imei}</span>
                        )}
                      </span>
                      <span className="font-mono font-semibold text-violet-900 shrink-0">{idr(t.harga)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs bg-violet-50 border border-violet-200 rounded p-2">
                    <b>Trade-in</b> — {n.trade_in_note || <span className="italic text-slate-500">tanpa catatan</span>}
                  </p>
                )
              )}
              {n.dp_id && (
                <div className="flex justify-between gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
                  data-testid="nota-detail-dp">
                  <span className="text-emerald-900 font-medium">Downpayment (DP)</span>
                  <span className="font-mono font-semibold text-emerald-900">
                    {n.dp_nominal != null ? idr(n.dp_nominal) : "seluruh sisa DP"}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-2 text-xs text-right space-y-0.5 text-slate-600">
              {totalTradeIn > 0 && <p>Total trade-in: <span className="font-mono font-semibold text-slate-900">{idr(totalTradeIn)}</span></p>}
              {n.harus_dibayar != null && (
                <p>Harus dibayar lewat metode: <span className="font-mono font-semibold text-slate-900" data-testid="nota-detail-harus-dibayar">{idr(n.harus_dibayar)}</span></p>
              )}
              {n.jumlah_baris_pembayaran != null && rinci && (
                <p>Jumlah baris pembayaran: <span className="font-mono font-semibold text-slate-900">{idr(n.jumlah_baris_pembayaran)}</span></p>
              )}
              {n.total_pembayaran_kasir != null && (
                <p className="text-emerald-700" data-testid="nota-detail-total-kasir">
                  Total Pembayaran dikonfirmasi kasir: <span className="font-mono font-semibold">{idr(n.total_pembayaran_kasir)}</span>
                </p>
              )}
              {!rinci && (n.harga_terima || 0) > 0 && (
                // Nota format LAMA: "Harga Terima" masih medan uangnya.
                <p>Harga Terima (format lama): <span className="font-mono font-semibold text-slate-900">{idr(n.harga_terima)}</span></p>
              )}
              {n.tempo_pembayaran && (
                <p className="text-amber-700">Tempo {String(n.tempo_pembayaran).replace("net_", "Net ")} — sisa jadi piutang</p>
              )}
            </div>
          </div>

          {/* Notes */}
          {n.catatan && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Catatan
              </p>
              <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2">{n.catatan}</p>
            </div>
          )}

          {/* Reject reason */}
          {n.status === "rejected" && n.reject_reason && (
            <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2">
              <b className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alasan penolakan:</b> {n.reject_reason}
              <p className="text-[10px] text-rose-600 mt-1">Oleh {n.rejected_by} · {n.rejected_at?.slice(0, 16)?.replace("T", " ")}</p>
            </div>
          )}

          {/* Meta */}
          <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            Dibuat oleh <b>{n.created_by_name || n.created_by_email}</b> · {n.created_at?.slice(0, 16)?.replace("T", " ")}
            {n.last_edited_at && <span> · edited {n.edit_count}x last {n.last_edited_at?.slice(0, 16)?.replace("T", " ")}</span>}
          </p>
        </div>
        <div className="border-t border-slate-100 p-4 flex flex-wrap justify-end gap-2">
          {canEdit && (
            <button onClick={onEdit} data-testid="btn-detail-edit"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {canApprove && n.status === "pending" && (
            <>
              <button onClick={onApprove} data-testid="btn-detail-approve"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Setujui & Transfer
              </button>
              <button onClick={onReject} data-testid="btn-detail-reject"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium">
                <XCircle className="w-3.5 h-3.5" /> Tolak
              </button>
            </>
          )}
          {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
            <button onClick={onDelete}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
              <Trash2 className="w-3.5 h-3.5" /> Hapus
            </button>
          )}
          <button onClick={onClose} className="h-9 px-3 rounded-lg border border-slate-200 text-xs">Tutup</button>
        </div>
      </div>
    </div>
  );
}

// ==================== Dialog Setujui nota (brief 22 Sep D1) ====================
// Dipakai KETIGA tombol Setujui (baris desktop, kartu HP, detail). Kasir wajib
// mengetik "Total Pembayaran" — uang yang BENAR-BENAR ia terima — dan server
// menolak approve (400, nota tetap pending) kalau kosong atau tidak sama dengan
// jumlah baris pembayaran. Angka pembandingnya dari `GET /nota/sales/{nid}/
// tagihan` (DP dibaca segar; draf format lama lewat perakit approve yang sama),
// bukan dihitung layar. Untuk nota bertempo yang belum dibayar, 0 adalah angka
// yang benar (D2) — kosong dan 0 dibedakan.
function DialogSetujuiNota({ n, onClose, onSelesai, onGagal }) {
  const [tagihan, setTagihan] = useState(null);     // respons GET .../tagihan
  const [memuat, setMemuat] = useState(true);
  const [total, setTotal] = useState("");
  const [kirim, setKirim] = useState(false);
  const [galat, setGalat] = useState("");

  useEffect(() => {
    let batal = false;
    (async () => {
      setMemuat(true);
      try {
        const { data } = await api.get(`/nota/sales/${n.id}/tagihan`);
        if (batal) return;
        setTagihan(data);
        // Isian kasir yang SUDAH tersimpan di draf (kasir sendiri yang membuat
        // nota ini) dipakai sebagai nilai awal — tetap bisa diubah.
        if (data?.total_pembayaran_kasir != null) setTotal(data.total_pembayaran_kasir);
      } catch (e) {
        if (!batal) setGalat(pesanGagalDariServer(e, "Gagal memuat tagihan nota"));
      } finally {
        if (!batal) setMemuat(false);
      }
    })();
    return () => { batal = true; };
  }, [n.id]);

  const r = tagihan?.rincian || null;
  const jumlah = tagihan?.jumlah_pembayaran;
  const pesanServer = tagihan?.pesan || [];
  const terisi = selisihTotalKasir(total, 0) !== null;
  const beda = terisi && jumlah != null && !totalKasirCocok(total, jumlah);
  const bolehSetujui = !memuat && !kirim && jumlah != null && terisi && !beda && pesanServer.length === 0;

  const setujui = async () => {
    if (!bolehSetujui) return;
    setKirim(true);
    setGalat("");
    try {
      const { data } = await api.post(`/nota/sales/${n.id}/approve`, { total_pembayaran: Number(total) });
      onSelesai(data);
    } catch (e) {
      // Kontrak G1: approve yang gagal di posting mengembalikan nota ke pending
      // dan menulis arsip kegagalan — alasan server + kode arsip ditampilkan
      // apa adanya supaya bisa ditelusuri, bukan pesan generik.
      setGalat(pesanGagalDariServer(e, "Gagal approve nota"));
      onGagal?.();
    } finally {
      setKirim(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl my-8" onClick={e => e.stopPropagation()}
        data-testid="dialog-setujui-nota">
        <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Setujui Nota — {n.nama_pelanggan}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded" data-testid="dialog-setujui-tutup">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {memuat ? (
            <p className="text-xs text-slate-500" data-testid="dialog-setujui-memuat">Memuat tagihan…</p>
          ) : (
            <>
              {r && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-0.5" data-testid="dialog-setujui-rincian">
                  <BarisRingkas label="Nilai nota" nilai={r.tagihan} tebal />
                  {r.trade_in > 0 && <BarisRingkas label="− Trade-in" nilai={-r.trade_in} kelas="text-violet-700" />}
                  {r.dp > 0 && <BarisRingkas label="− Downpayment (DP)" nilai={-r.dp} kelas="text-emerald-700" />}
                  <BarisRingkas label="Harus dibayar" nilai={r.harus_dibayar} />
                </div>
              )}
              {(n.payments || []).filter(p => (Number(p.amount) || 0) > 0).length > 0 && (
                <div className="text-xs space-y-0.5">
                  {(n.payments || []).filter(p => (Number(p.amount) || 0) > 0).map((p, i) => (
                    <div key={i} className="flex justify-between gap-2 text-slate-600">
                      <span>{p.method}{p.keterangan ? ` · ${p.keterangan}` : ""}</span>
                      <span className="font-mono">{idr(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {jumlah != null && (
                <p className="text-xs text-slate-700">
                  Jumlah pembayaran di nota:{" "}
                  <b className="font-mono" data-testid="dialog-setujui-jumlah">{idr(jumlah)}</b>
                  {n.tempo_pembayaran && r && r.harus_dibayar - jumlah > 1 && (
                    <span className="block text-[11px] text-amber-700">
                      Nota bertempo — sisa {idr(r.harus_dibayar - jumlah)} jadi piutang.
                    </span>
                  )}
                </p>
              )}
              {pesanServer.map((m, i) => (
                <p key={i} className="text-xs text-rose-700 flex items-start gap-1.5" data-testid="dialog-setujui-pesan">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {m}
                </p>
              ))}
              <Field label="Total Pembayaran (uang yang benar-benar diterima) *">
                <InputRupiah bolehKosong value={total} onChange={v => setTotal(v === null ? "" : v)}
                  testid="dialog-setujui-total" placeholder="ketik jumlahnya"
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-right font-mono ${
                    beda ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} />
              </Field>
              {beda && (
                <p className="text-xs text-rose-700 flex items-start gap-1.5" data-testid="dialog-setujui-alert-beda">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Total Pembayaran {idr(total)} tidak sama dengan jumlah pembayaran di nota {idr(jumlah)} —
                  periksa lagi uang yang diterima, atau tolak/edit notanya.
                </p>
              )}
            </>
          )}
          {galat && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="dialog-setujui-galat">
              {galat}
            </p>
          )}
        </div>
        <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={setujui} disabled={!bolehSetujui} data-testid="btn-setujui-konfirmasi"
            className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {kirim ? "Memproses..." : "Setujui & Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Satu baris ringkasan angka (label kiri, rupiah kanan). */
function BarisRingkas({ label, nilai, tebal = false, kelas = "text-slate-600", testid }) {
  return (
    <div className={`flex justify-between gap-3 ${tebal ? "font-semibold text-slate-900" : kelas}`}>
      <span>{label}</span>
      <span className="font-mono" data-testid={testid}>{nilai < 0 ? `−${idr(-nilai)}` : idr(nilai)}</span>
    </div>
  );
}

// ==================== Pemilih IMEI per baris -> unit inventory ====================
// Brief bos poin 4: IMEI HANYA boleh DIPILIH dari unit yang terdaftar &
// berstatus tersedia di inventory — tidak lagi diketik bebas. Beda dari
// ItemCariField (components/pos/ItemCariField.jsx, kontrak G2 bagian C):
// mengetik di sini TIDAK PERNAH langsung mengisi `item.imei` (itu yang
// membuatnya "ketik bebas"), field baru terisi begitu kasir mengklik salah
// satu SARAN hasil pencarian. Sekali terisi, tampil sebagai badge (mirip
// badge katalog) — untuk mengubahnya, lepas dulu lewat "×".
//
// Gerbang render yang SEBENARNYA dipasang di pemanggil: `bolehLookupUnit &&
// it.sku` — SETIAP baris yang sudah tertaut SKU menampilkan komponen ini,
// tanpa membedakan `tipe:"produk"`/`"kit"` dari `tipe:"unit"`. Itu memang
// TIDAK BISA dibedakan di sini: `r.tipe` dari hasil pencarian cuma dipakai
// sesaat di dalam `ItemCariField.pilih` (components/pos/ItemCariField.jsx)
// untuk memutuskan field apa saja yang diisi — nilainya sendiri TIDAK
// disimpan ke state baris. Yang membedakan tampilan komponen ini bukan
// gerbang render, tapi `item.imei`: baris `tipe:"unit"` datang dari
// `ItemCariField.pilih` dengan `imei` SUDAH terisi sekaligus, jadi langsung
// jatuh ke cabang badge (baca-saja + tombol lepas) di bawah; baris
// `tipe:"produk"`/`"kit"` datang dengan `imei` kosong, jatuh ke cabang kotak
// pencarian. Melepas badge ("×") pada baris `tipe:"unit"` membuka kembali
// pencarian supaya kasir bisa memilih unit lain.
//
// CATATAN BACKEND (baca sebelum mengandalkan ini sebagai satu-satunya
// pagar): validasi `_tolak_stok_kosong` (routers/sales.py, TIDAK disentuh
// dari sisi frontend ini) menolak IMEI yang tidak terdaftar/tidak
// "tersedia" per BARIS, tapi TIDAK mengunci/mereservasi unit — dua nota
// terpisah yang kebetulan memilih IMEI yang sama di baris yang berbeda
// keduanya akan lolos validasi server (unit tidak pernah ditandai "terjual"
// oleh sales.py, lihat routers/retur.py "BATASAN YANG SENGAJA DIPUTUSKAN").
// Yang bisa dicegah dari sini HANYA duplikat DALAM SATU nota yang sedang
// diisi (lewat `imeiTerpakaiLain`) — bukan lintas nota/transaksi.
function ItemImeiField({ index, item, bolehLookup, imeiTerpakaiLain, onPatch, warehouseId, onWarehouseHint }) {
  // REKOMENDASI OTOMATIS (brief owner 29 Agu, Input Nota poin 1): begitu
  // produknya dipilih, unit yang tersedia untuk SKU itu langsung ditawarkan —
  // kasir tidak perlu mengetik apa pun. Sebelumnya daftar baru muncul setelah
  // 3 huruf diketik, dan kasir yang tidak tahu IMEI-nya tidak akan pernah
  // melihat pilihan apa pun.
  //
  // Aman terhadap "menguras gudang": permintaannya SELALU membawa `sku`, dan
  // backend hanya membolehkan panggilan tanpa `q` kalau `sku` disebut.
  useEffect(() => {
    if (!bolehLookup || !item.sku || item.imei) { setSaran([]); return; }
    let batal = false;
    (async () => {
      setMencari(true);
      try {
        // `warehouse_id` (kontrak G1-C EppleHub, satu nota satu gudang): kalau
        // gudang nota sudah diketahui, rekomendasi hanya dari gudang itu.
        const { data } = await api.get("/inventory/units/pilihan-jual",
                                       { params: { sku: item.sku, warehouse_id: warehouseId || undefined } });
        if (!batal) setSaran(data.items || []);
      } catch {
        if (!batal) setSaran([]);
      } finally {
        if (!batal) setMencari(false);
      }
    })();
    return () => { batal = true; };
  }, [bolehLookup, item.sku, item.imei, warehouseId]);
  const [cari, setCari] = useState("");
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cariUnit = (teks) => {
    setCari(teks);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 3) { setSaran([]); setMencari(false); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual",
                                       { params: { q, sku: item.sku, warehouse_id: warehouseId || undefined } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      } finally {
        if (seq === reqSeq.current) setMencari(false);
      }
    }, 300);
  };

  const pilih = (u) => {
    // G1 (keputusan 23 Sep): unit second yang punya Harga Jual Unit sendiri
    // mengisi harga baris dengan harga itu (kasir tetap bisa mengubahnya).
    // Tanpa medan itu dari server = perilaku lama persis (harga tidak disentuh).
    // Baris PAKET tidak pernah sampai ke sini (pemilih ini tidak dirender untuk
    // paket), jadi harga paket yang terkunci (F3) tidak tertimpa harga unit.
    onPatch({ imei: u.identifier, ...(u.harga_unit_khusus ? { harga: u.harga_bawaan } : {}) });
    // Unit pertama yang dipilih mengunci gudang nota (pola sama dengan
    // ItemCariField) — rekomendasi baris berikutnya ikut gudang itu.
    if (onWarehouseHint && u.warehouse_id) onWarehouseHint(u.warehouse_id);
    setCari("");
    setSaran([]);
  };

  if (item.imei) {
    return (
      <div className="mt-1">
        <span data-testid={`item-imei-badge-${index}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
          IMEI {item.imei}
          <button type="button" onClick={() => onPatch({ imei: "" })}
            title="Lepas pilihan IMEI"
            className="text-violet-600 hover:text-violet-900 leading-none">×</button>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <input value={cari} onChange={e => cariUnit(e.target.value)}
        placeholder={saran.length > 0 ? `Pilih 1 dari ${saran.length} IMEI tersedia, atau ketik untuk menyaring…`
                                      : "Cari IMEI/SN dari stok…"}
        data-testid={`item-imei-cari-${index}`}
        className="h-8 px-2 rounded border border-slate-200 text-[11px] w-full font-mono" />
      {saran.length > 0 && (
        <div data-testid={`item-imei-rekomendasi-${index}`}
             className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {saran.map((u, n) => {
            const dipakai = imeiTerpakaiLain?.has(u.identifier);
            return (
              <button key={u.id} type="button" disabled={dipakai}
                onClick={() => { if (!dipakai) pilih(u); }}
                title={dipakai ? "Unit ini sudah dipilih di baris lain pada nota ini" : ""}
                data-testid={`item-imei-saran-${index}-${n}`}
                className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-slate-50 last:border-0 ${
                  dipakai ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-violet-50"}`}>
                <span className="font-mono font-medium text-slate-900">{u.identifier}</span>
                {u.warehouse_nama && <span className="text-slate-400 ml-1.5">· {u.warehouse_nama}</span>}
                {dipakai && <span className="text-rose-600 ml-1.5">sudah dipilih di baris lain</span>}
              </button>
            );
          })}
        </div>
      )}
      {!mencari && saran.length === 0 && (
        <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`item-imei-kosong-${index}`}>
          {cari.trim().length >= 3
            ? "Tidak ada unit tersedia yang cocok."
            : "Belum ada unit ber-IMEI yang tersedia untuk produk ini."}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-slate-900 font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ I, label, value, tint }) {
  // Di HP: kartu membulat berbayang lembut tanpa garis (bahasa desain baru,
  // redesign 24 Agu 2026). Di desktop: persis seperti sebelumnya.
  return (
    <div className="bg-white border-0 md:border md:border-slate-200 rounded-3xl md:rounded-xl p-4 shadow-[0_1px_2px_rgba(20,25,34,.04),0_8px_24px_rgba(20,25,34,.06)] md:shadow-none">
      <div className={`w-8 h-8 rounded-full md:rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <I className="w-4 h-4" />
      </div>
      <p className="text-[11px] md:text-[10px] font-semibold md:uppercase md:tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}
